import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Form,
  Table,
  Typography,
  Divider,
  Space,
  Modal,
  Upload,
  message,
  Alert,
  Tag,
  Spin,
  Radio,
  Popover,
  Checkbox,
  Empty,
  Tooltip,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  SaveOutlined,
  SendOutlined,
  EyeOutlined,
  DownloadOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined,
  CalculatorOutlined,
  EditOutlined,
  ScheduleOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
  RightOutlined,
  SettingOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { formatCurrency, formatIndianNumber, indianCurrencyFormatter, indianCurrencyParser } from "../../utils/Utils";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";
import RABillApis from "../../api/WorkOrderManagement/RABillApis";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";
import WorkOrderApprovalDrawer from "../work-orders/workOrderCreation/WorkOrderApprovalDrawer";
import sequenceApis from "../../api/master/sequence";
import { Button } from "reactstrap";
import { Icon } from "../../components/Component";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface WorkLineStage {
  workOrderLineStageId: number;
  stageTitle: string;
  splitPercentage: number;
  stageRate: number; // Stage rate (rate * splitPercentage / 100)
  subtotal: number; // Approved amount for this stage
  totalBilledValueExclTax: number; // Cumulative billed amount (excl tax)
  totalPendingApprovalBilledValueExclTax: number; // Pending approval amount
  balanceBilledValue: number; // Available to bill
  approvedQty: number; // Approved quantity
  cumulativeBilledQty: number; // Cumulative billed quantity
  pendingApprovalQty: number; // Pending approval quantity
  onHoldQty: number; // On hold quantity
  AvailabletoBillQty: number; // Available quantity to bill
  thisBillQuantity: number; // Current bill quantity (user input)
  thisBillAmount: number; // Current bill amount (calculated: thisBillQuantity * stageRate)
  remarks: string; // Stage remarks
  error?: string; // Validation error message
}

interface WorkLine {
  id: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  uom: string;
  contractQuantity: number;
  cumulativeApprovedQuantity: number;
  previousBilledQuantity: number;
  pendingApprovalQuantity: number;
  onHoldQuantity: number;
  availableQuantity: number;
  thisBillQuantity: number;
  rate: number;
  lineSubtotal: number;
  taxPercentage: number;
  taxValue: number;
  lineTotal: number;
  taxAmount?: number;
  sgstRate?: number;
  cgstRate?: number;
  igstRate?: number;
  sgstAmount?: number;
  cgstAmount?: number;
  igstAmount?: number;
  taxTypeName?: string;
  // Stage-wise billing
  stages?: WorkLineStage[];
  hasStages?: boolean;
  remarks?: string;
  error?: string; // Validation error message
}

interface AdditionalCharge {
  id: string;
  chargeName: string;
  amount: number;
}

const CreateRABillDetails: React.FC = () => {
  const { woId } = useParams<{ woId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [workOrder, setWorkOrder] = useState<any>(null);
  const [workLines, setWorkLines] = useState<WorkLine[]>([]);
  const [deductions, setDeductions] = useState<any[]>([]);
  const [totals, setTotals] = useState<any | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showValidation, setShowValidation] = useState(false);
  const [raBillNumber, setRABillNumber] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [notes, setNotes] = useState("");
  const [statesList, setStatesList] = useState<any[]>([]);
  const [approvalRequired, setApprovalRequired] = useState<string>("false");
  const [approvals, setApprovals] = useState<{ open: boolean; data: any[] }>({
    open: false,
    data: [],
  });
  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);

  const [sequenceAccess, setSequenceAccess] = useState<any>(null);
  const [activeSequences, setActiveSequences] = useState<any[]>([]);
  const [numberingMode, setNumberingMode] = useState<'auto' | 'manual'>('auto');
  const [showNumberingModal, setShowNumberingModal] = useState(false);
  const [tempNumberingMode, setTempNumberingMode] = useState<'auto' | 'manual'>('auto');

  const [woSummary, setWOSummary] = useState<any>(null); // Work Order summary from API
  const [additionalCharges, setAdditionalCharges] = useState<
    AdditionalCharge[]
  >([]);
  const [selectedRiskflassItems, setSelectedRiskflassItems] = useState<
    Set<string>
  >(new Set());
  const [selectedAdvanceItems, setSelectedAdvanceItems] = useState<Set<string>>(
    new Set()
  );
  const [riskflassItemAmounts, setRiskflassItemAmounts] = useState<{
    [key: string]: number;
  }>({});
  const [riskflassItemRemarks, setRiskflassItemRemarks] = useState<{
    [key: string]: string;
  }>({});
  const [advanceItemAmounts, setAdvanceItemAmounts] = useState<{
    [key: string]: number;
  }>({});
  const [advanceItemRemarks, setAdvanceItemRemarks] = useState<{
    [key: string]: string;
  }>({});
  const isAutoCorrectingRef = useRef<{ [key: string]: boolean }>({});

  useEffect(() => {
    // Always load data for demo purposes, use woId if available or default
    loadWorkOrderDetails();
    loadStatesList();
  }, []);

  const loadStatesList = async () => {
    try {
      const response = await dropdownApis.getStatesByCountry(1); // India country ID
      if (response.data) {
        setStatesList(response.data);
      }
    } catch (error) {
      console.error("Error loading states list:", error);
    }
  };

  const fetchSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "RA Bill" &&
          new Date(item.endDate) > today &&
          item.status === "Active"
      );
      setActiveSequences(_sequenceAccess || []);
      // Set initial numbering mode based on available sequences
      if (_sequenceAccess && _sequenceAccess.length > 0 && workOrder?.site) {
        // Site-level first
        const hasSiteSequence = _sequenceAccess.some(
          (seq: any) => seq.level === "Site" && seq.siteId?.toString() === workOrder?.site?.toString()
        );
        if (hasSiteSequence) {
          setNumberingMode('auto');
        } else {
          // Org-level fallback
          const hasOrgSequence = _sequenceAccess.some(
            (seq: any) => seq.level === "Org"
          );
          setNumberingMode(hasOrgSequence ? 'auto' : 'manual');
        }
      } else {
        setNumberingMode('manual');
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      setActiveSequences([]);
      setNumberingMode('manual');
    }
  };

  const getCurrentSiteSequence = () => {
    if (!workOrder?.site || activeSequences.length === 0) return null;

    const siteSequence = activeSequences.find(
      (seq) =>
        seq.level === "Site" &&
        seq.siteId?.toString() === workOrder?.site?.toString()
    );

    if (siteSequence) return siteSequence;

    const orgSequence = activeSequences.find(
      (seq) => seq.level === "Org"
    );

    if (orgSequence) return orgSequence;

    return null;
  };

  const currentSiteSequence = getCurrentSiteSequence();
  const canAuto = !!workOrder?.site && !!currentSiteSequence;

  useEffect(() => {
    const existingValue = form.getFieldValue("raBillNumber");

    if (numberingMode === 'auto' && currentSiteSequence && !existingValue) {
      const { prefix, nextNumber } = currentSiteSequence;
      const poNumber = `${prefix}`;
      form.setFieldsValue({ raBillNumber: poNumber });
    }
  }, [numberingMode, currentSiteSequence]);

  const prevSiteRef = useRef();

  useEffect(() => {
    const prevSite = prevSiteRef.current;

    // Only run if site actually changed
    if (prevSite && prevSite !== workOrder?.site) {
      form.setFieldsValue({ raBillNumber: "" });
    }

    if (!workOrder?.site) {
      setNumberingMode("manual");
    } else {
      const siteSequence = getCurrentSiteSequence();
      setNumberingMode(siteSequence ? "auto" : "manual");
    }

    prevSiteRef.current = workOrder?.site;
  }, [workOrder?.site]);

  // Re-fetch sequences when component mounts and when site changes
  useEffect(() => {
    if (workOrder?.site) {
      fetchSequenceAccess();
    }
  }, [workOrder?.site]);

  useEffect(() => {
    setRABillNumber('');
    form.setFieldsValue({ raBillNumber: '' });
  }, [workOrder?.site, form]);

  useEffect(() => {
    if (workOrder?.site) {
      if (currentSiteSequence) {
        setNumberingMode('auto');
      } else {
        setNumberingMode('manual');
      }
    }
  }, [workOrder?.site, currentSiteSequence]);

  // useEffect(() => {
  //   if (workLines.length > 0 && deductions.length > 0) {
  //     recalculateTotals(workLines, deductions);
  //   }
  // }, [workLines, deductions]);

  // Auto-populate form values when work order is loaded
  useEffect(() => {
    if (workOrder) {
      form.setFieldsValue({
        raBillNumber: raBillNumber,
        woNumber: workOrder.woNumber,
        contractor: workOrder.contractor,
        placeOfSupply: workOrder.placeOfSupply,
        contractorGSTIN: workOrder.contractorGSTIN,
        companyGstin: workOrder.companyGstin,
        taxRegime: "CGST/SGST",
      });
    }
  }, [workOrder, raBillNumber, form]);

  const mapApiResponseToRABill = (apiData: any) => {
    const workOrder = {
      id: apiData.workOrderId,
      woNumber: apiData.workOrderNumber,
      contractor: apiData.contractorName,
      contractorId: apiData.contractorId,
      site: apiData.siteId,
      project: apiData.workOrderTitle,
      section: apiData.workOrderDescription,
      woTotalValue: apiData.grossValue || 0,
      cumulativeWorkValue: apiData.totalExecutedValue || 0,
      cumulativeWorkDone: apiData.progressPercent || 0,
      previousRABillsValue: apiData.totalBilledValue || 0,
      contractorGSTIN: apiData.contractorGSTIN || "",
      companyGstin: apiData.companyGstin,
      placeOfSupply: apiData.placeOfSupply || "",
      plannedStartDate: apiData.plannedStartDate
        ? dayjs(apiData.plannedStartDate)
        : null,
      plannedEndDate: apiData.plannedEndDate
        ? dayjs(apiData.plannedEndDate)
        : null,
      tdsRate: apiData.tdsRate || 0,
      tcsRate: apiData.tcsRate || 0,
      tdsTaxName: apiData.tdsTaxName || "",
      tcsTaxName: apiData.tcsTaxName || "",
      contractDate: apiData.contractDate || null,
      lastRaBillDate: apiData.lastRaBillDate || null,
    };

    const workLines: WorkLine[] = (apiData.workOrderLines || []).map(
      (line: any) => {
        // Calculate total tax percentage from individual rates
        const totalTaxRate = line.igstRate
          ? line.igstRate
          : (line.sgstRate || 0) + (line.cgstRate || 0);

        console.log("Mapping work line:", {
          workOrderLineId: line.workOrderLineId,
          taskTitle: line.taskTitle,
          rate: line.rate,
          quantity: line.quantity,
          lineValue: line.lineValue,
        });

        // Map stages for stage-wise billing
        const hasStages = line.stages && line.stages.length > 0;
        const stages: WorkLineStage[] = hasStages
          ? line.stages.map((stage: any) => ({
            workOrderLineStageId: stage.workOrderLineStageId,
            stageTitle: stage.stageTitle || "",
            splitPercentage: stage.splitPercentage || 0,
            stageRate:
              ((line.rate || 0) * (stage.splitPercentage || 0)) / 100, // Calculate stage rate
            approvedQty: stage.totalExecutedQty || 0,
            cumulativeBilledQty: stage.totalBilledQty || 0,
            pendingApprovalQty: stage.totalPendingApprovalBilledQty || 0,
            onHoldQty: stage.totalOnHoldQty || 0,
            AvailabletoBillQty:
              (stage.totalExecutedQty || 0) -
              (Number(stage.totalBilledQty) || 0) -
              (Number(stage.totalPendingApprovalBilledQty) || 0) -
              (Number(stage.totalOnHoldQty) || 0),
            subtotal: stage.subtotal || 0,
            totalBilledValueExclTax: stage.totalBilledValueExclTax || 0,
            totalPendingApprovalBilledValueExclTax:
              stage.totalPendingApprovalBilledValueExclTax || 0,
            balanceBilledValue: stage.balanceBilledValue || 0,
            thisBillQuantity: 0,
            thisBillAmount: 0,
            remarks: "",
          }))
          : [];

        return {
          id: line.workOrderLineId,
          workLineNumber: line.workOrderLineId,
          workLineTitle: line.taskTitle,
          description: line.description,
          uom: line.uomName,
          contractQuantity: line.quantity || 0,
          cumulativeApprovedQuantity: line.totalExecutedQty || 0,
          previousBilledQuantity: line.totalBilledQty || 0,
          pendingApprovalQuantity: line.totalPendingApprovalBilledQty || 0,
          onHoldQuantity: line.totalOnHoldQty || 0,
          availableQuantity:
            (line.totalExecutedQty || 0) -
            (line.totalBilledQty || 0) -
            (line.totalPendingApprovalBilledQty || 0) -
            (line.totalOnHoldQty || 0),
          thisBillQuantity: 0,
          rate: line.rate || 0,
          taxType: line.taxId?.toString() || "",
          taxTypeName: line.taxName || "",
          taxPercentage: totalTaxRate, // Calculate from sgst+cgst or igst
          taxAmount: line.taxAmount || 0,
          sgstRate: line.sgstRate || 0,
          cgstRate: line.cgstRate || 0,
          igstRate: line.igstRate || 0,
          sgstAmount: line.sgstAmount || 0,
          cgstAmount: line.cgstAmount || 0,
          igstAmount: line.igstAmount || 0,
          lineValue: line.lineValue || 0,
          lineSubtotal: 0, // Will be calculated when quantity is entered
          taxValue: 0, // Will be calculated when quantity is entered
          lineTotal: 0, // Will be calculated when quantity is entered
          // Stage-wise billing
          stages,
          hasStages,
          remarks: "",
        };
      }
    );

    // Map payment conditions to deductions
    const deductions: any[] = (apiData.workOrderPaymentConditions || []).map(
      (condition: any, index: number) => ({
        id: condition.workOrderPaymentTermId
          ? `ded_${condition.workOrderPaymentTermId}`
          : `ded_idx_${index}`,
        raBillId: "",
        type: condition.conditionType,
        amount: condition.calculatedAmount || 0,
        percentage: condition.percentValue || 0,
        percentValue: condition.percentValue || 0, // Keep this for recalculation logic
        calculatedAmount: condition.calculatedAmount || 0, // Keep original calculated amount
        remarks: condition.notes || condition.remarks || "",
        maxAmount: condition.calculatedAmount || undefined, // For mobilization, this is the max recoverable amount
        // Remaining balance to be recovered
        // Fallback: if balanceMobilizationRecovery is 0 but amountValue exists and nothing recovered yet,
        // use amountValue - totalRecoveredAmount - pendingMobilizationRecovery as balance
        balanceMobilizationRecovery:
          condition.balanceMobilizationRecovery ||
          (condition.conditionType === "Mobilization" && condition.amountValue > 0
            ? condition.amountValue -
              (condition.totalRecoveredAmount || 0) -
              (condition.pendingMobilizationRecovery || 0)
            : 0),
        recoveryPercentRate: condition.recoveryPercentRate || 0, // Recovery percentage per RA Bill
        isManual: condition.isPercent === false && condition.percentValue === 0,
        calculationType: condition.isPercent ? "Percentage" : "Amount",
        taxableBase: condition.taxableBase || "Pre-tax",
        tdsSection: condition.tdsSection || undefined,
        sectionDescription: condition.sectionDescription || undefined,
        thresholdAmount: condition.thresholdAmount || 0,
        recoveryType: condition.recoveryType || undefined,
        interestCharged: condition.interestCharged || false,
        interestRate: condition.interestRate || 0,
        recoveryCapPercent: condition.recoveryCapPercent || 0,
      })
    );

    return { workOrder, workLines, deductions };
  };

  const loadWorkOrderDetails = async () => {
    setLoading(true);
    try {
      const response: any = await workOrderApis.getWorkOrderById(woId);

      if (response.data) {
        const { workOrder, workLines, deductions } = mapApiResponseToRABill(
          response.data
        );
        // Add riskflass and advance items from API response (workOrderAdvanceDeductions)
        const workOrderWithItems = {
          ...workOrder,
          riskflassItems:
            response.data.workOrderAdvanceDeductions?.filter(
              (item: any) =>
                item.type === "Risk Flag" && item.isChargable === true
            ) || [],
          advanceItems:
            response.data.workOrderAdvanceDeductions?.filter(
              (item: any) =>
                item.type === "Advance" || item.type === "Mobilization Advance"
            ) || [],
        };
        setWorkOrder(workOrderWithItems);
        setWorkLines(workLines);
        setDeductions(deductions);

        // Initialize riskflass and advance item amounts with balanceAmount
        const initialRiskflassAmounts: { [key: string]: number } = {};
        workOrderWithItems.riskflassItems?.forEach((item: any) => {
          const itemId =
            item.workOrderAdvanceDeductionId?.toString() ||
            item.id?.toString() ||
            "";
          const totalAmount = item.totalAmount || 0;
          const totalRecovered = item.totalRecoveredAmount || 0;
          const totalPending = item.totalPendingRecoveredAmount || 0;
          const cumulativeRecoveredAmount = totalRecovered + totalPending;
          const balanceAmount = totalAmount - cumulativeRecoveredAmount;
          initialRiskflassAmounts[itemId] = balanceAmount;
        });
        setRiskflassItemAmounts(initialRiskflassAmounts);

        const initialAdvanceAmounts: { [key: string]: number } = {};
        workOrderWithItems.advanceItems?.forEach((item: any) => {
          const itemId =
            item.workOrderAdvanceDeductionId?.toString() ||
            item.id?.toString() ||
            "";
          const totalAmount = item.totalAmount || 0;
          const totalRecovered = item.totalRecoveredAmount || 0;
          const totalPending = item.totalPendingRecoveredAmount || 0;
          const cumulativeRecoveredAmount = totalRecovered + totalPending;
          const balanceAmount = totalAmount - cumulativeRecoveredAmount;
          initialAdvanceAmounts[itemId] = balanceAmount;
        });
        setAdvanceItemAmounts(initialAdvanceAmounts);

        // Initialize totals immediately
        const initialTotals: any = {
          raBillId: "DRAFT",
          subtotalExclTax: 0,
          taxTotal: 0,
          grossTotal: 0,
          mobilizationRecovery: 0,
          retention: 0,
          onHold: 0,
          tds: 0,
          totalDeductions: 0,
          roundOff: 0,
          netPayable: 0,
        };
        setTotals(initialTotals);

        // // Force recalculation after state is set
        // setTimeout(() => {
        //   recalculateTotals(workLines, deductions);
        // }, 0);
      } else {
        message.error("No work order data found");
      }
    } catch (error) {
      console.error("Error loading work order:", error);
      message.error("Failed to load work order details");
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (value: number | null, lineId: string) => {
    console.log("handleQuantityChange called:", {
      value,
      lineId,
      workLinesLength: workLines.length,
    });

    if (value === null) value = 0;

    const updatedLines = workLines.map((line: any) => {
      console.log("line:", line);
      if (line.id === lineId) {
        const newQuantity = value!;

        // Validate: quantity should not exceed available quantity
        const hasError = newQuantity > line.availableQuantity;

        const subtotal = newQuantity * line.rate;
        console.log("New subtotal::::", subtotal, line.taxPercentage);

        // Calculate tax using total tax percentage (CGST + SGST or IGST)
        const totalTaxRate = line.igstRate
          ? line.igstRate
          : (line.sgstRate || 0) + (line.cgstRate || 0);
        const taxValue = subtotal * (totalTaxRate / 100);
        const lineTotal = subtotal + taxValue;

        console.log("Updating line:", {
          lineId,
          newQuantity,
          rate: line.rate,
          subtotal,
          totalTaxRate,
          taxValue,
          lineTotal,
        });

        return {
          ...line,
          thisBillQuantity: newQuantity,
          lineSubtotal: subtotal,
          taxValue: taxValue,
          lineTotal: lineTotal,
          error: hasError
            ? `Max allowed: ${line.availableQuantity} ${line.uom}`
            : "",
        };
      }
      return line;
    });

    setWorkLines(updatedLines);
    recalculateTotals(updatedLines, deductions);
  };

  // Handler for stage-wise billed quantity change
  const handleStageBillQuantityChange = (
    lineId: string,
    stageId: number,
    value: number | null,
    stageRate: number
  ) => {
    const newQuantity = value ?? 0;
    const calculatedAmount = newQuantity * stageRate; // Calculate This Bill Amount = This Bill Qty * Stage Rate

    setWorkLines((prevLines) => {
      const updatedLines = prevLines.map((line) => {
        if (line.id !== lineId) return line;

        const updatedStages =
          line.stages?.map((stage) => {
            if (stage.workOrderLineStageId === stageId) {
              // Validate: quantity should not exceed available quantity to bill
              const hasError = newQuantity > stage.AvailabletoBillQty;

              return {
                ...stage,
                thisBillQuantity: newQuantity,
                thisBillAmount: calculatedAmount,
                error: hasError
                  ? `Max allowed: ${stage.AvailabletoBillQty}`
                  : "",
              };
            }
            return stage;
          }) || [];

        // Calculate total bill amount from all stages
        const totalStageBillAmount = updatedStages.reduce(
          (sum, stage) => sum + (stage.thisBillAmount || 0),
          0
        );

        // Calculate tax using total tax percentage
        const totalTaxRate = line.igstRate
          ? line.igstRate
          : (line.sgstRate || 0) + (line.cgstRate || 0);
        const taxValue = totalStageBillAmount * (totalTaxRate / 100);
        const lineTotal = totalStageBillAmount + taxValue;

        return {
          ...line,
          stages: updatedStages,
          lineSubtotal: totalStageBillAmount,
          taxValue: taxValue,
          lineTotal: lineTotal,
        };
      });

      // Recalculate totals with updated lines
      setTimeout(() => recalculateTotals(updatedLines, deductions), 0);

      return updatedLines;
    });
  };

  // Handler for stage remarks change
  const handleStageRemarksChange = (
    lineId: string,
    stageId: number,
    value: string
  ) => {
    setWorkLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id !== lineId) return line;

        const updatedStages =
          line.stages?.map((stage) =>
            stage.workOrderLineStageId === stageId
              ? { ...stage, remarks: value }
              : stage
          ) || [];

        return { ...line, stages: updatedStages };
      })
    );
  };

  // Handler for line remarks change
  const handleLineRemarksChange = (lineId: string, value: string) => {
    setWorkLines((prevLines) =>
      prevLines.map((line) =>
        line.id === lineId ? { ...line, remarks: value } : line
      )
    );
  };

  // Additional Charges Handlers
  const handleAddAdditionalCharge = () => {
    const newCharge: AdditionalCharge = {
      id: `charge_${Date.now()}`,
      chargeName: "",
      amount: 0,
    };
    setAdditionalCharges((prev) => [...prev, newCharge]);
  };

  const handleUpdateAdditionalCharge = (
    id: string,
    field: "chargeName" | "amount",
    value: string | number
  ) => {
    const updatedCharges = additionalCharges.map((charge) =>
      charge.id === id ? { ...charge, [field]: value } : charge
    );
    setAdditionalCharges(updatedCharges);
    // Recalculate totals when amount changes
    if (field === "amount") {
      recalculateTotals(workLines, deductions, updatedCharges);
    }
  };

  const handleRemoveAdditionalCharge = (id: string) => {
    const updatedCharges = additionalCharges.filter(
      (charge) => charge.id !== id
    );
    setAdditionalCharges(updatedCharges);
    // Recalculate totals after removing charge
    recalculateTotals(workLines, deductions, updatedCharges);
  };

  // Calculate total additional charges
  const getTotalAdditionalCharges = () => {
    return additionalCharges.reduce(
      (sum, charge) => sum + (charge.amount || 0),
      0
    );
  };

  const validateRABill = (): string[] => {
    const errors: string[] = [];

    // Check if at least one line has quantity > 0
    const hasPositiveValue = workLines.some((line) => {
      if (line.hasStages && line.stages && line.stages.length > 0) {
        // For stage-wise lines, check if any stage has quantity > 0
        return line.stages.some((stage) => stage.thisBillQuantity > 0);
      }
      return line.thisBillQuantity > 0;
    });
    if (!hasPositiveValue) {
      errors.push("At least one line must have This Bill Quantity > 0");
    }

    // Check for validation errors in lines
    workLines.forEach((line) => {
      // Check line-level errors
      if (line.error) {
        errors.push(`Line ${line.workLineTitle}: ${line.error}`);
      }

      if (line.hasStages && line.stages && line.stages.length > 0) {
        // For stage-wise lines, check each stage for errors
        line.stages.forEach((stage: any) => {
          if (stage.error) {
            errors.push(
              `Stage "${stage.stageTitle}" in line ${line.workLineTitle}: ${stage.error}`
            );
          }
        });
      }
    });

    // Check form validation
    const formValues = form.getFieldsValue();
    if (!formValues.raPeriodFrom || !formValues.raPeriodTo) {
      errors.push("RA Period From and To dates are required");
    }

    if (!formValues.paymentDueDate) {
      errors.push("Payment Due Date is required");
    }

    // if (!formValues.contractorGSTIN) {
    //   errors.push("Contractor GSTIN is required");
    // }

    // Check approval configuration if approval required
    if (approvalRequired === "true" && approvals.data.length === 0) {
      errors.push("Please configure approvals before submitting");
    }

    // Validate additional charges
    additionalCharges.forEach((charge, index) => {
      if (charge.amount > 0 && !charge.chargeName.trim()) {
        errors.push(
          `Additional charge #${index + 1}: Please enter a charge name`
        );
      }
    });

    return errors;
  };

  // Check if there are any validation errors in work lines
  const hasValidationErrors = () => {
    return workLines.some((line) => {
      // Check line-level errors
      if (line.error) return true;

      // Check stage-level errors
      if (line.hasStages && line.stages && line.stages.length > 0) {
        return line.stages.some((stage: any) => stage.error);
      }

      return false;
    });
  };

  const handleSubmit = async () => {
    const errors = validateRABill();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowValidation(true);

      // Show detailed error message
      Swal.fire({
        icon: "error",
        title: "Validation Errors",
        html: `
          <div style="text-align: left;">
            <p>Please fix the following errors before submitting:</p>
            <ul style="padding-left: 20px;">
              ${errors.map((error) => `<li>${error}</li>`).join("")}
            </ul>
          </div>
        `,
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      setLoading(true);
      const formValues = form.getFieldsValue();

      // Prepare RA Bill data for API
      const raBillData = {
        workOrderId: parseInt(woId as string),
        // ...(raBillNumber && { raBillSequenceId: raBillNumber }),
        raBillSequenceId: numberingMode === 'auto' ? null : (raBillNumber || null),
        raPeriodFrom: formValues.raPeriodFrom
          ? dayjs(formValues.raPeriodFrom).format("YYYY-MM-DD")
          : "",
        raPeriodTo: formValues.raPeriodTo
          ? dayjs(formValues.raPeriodTo).format("YYYY-MM-DD")
          : "",
        paymentDueDate: formValues.paymentDueDate
          ? dayjs(formValues.paymentDueDate).format("YYYY-MM-DD")
          : "",
        placeOfSupply:
          formValues.placeOfSupply || workOrder?.placeOfSupply || "",
        contractorGstin:
          formValues.contractorGSTIN || workOrder?.contractorGSTIN || "",
        companyGstin: formValues.companyGstin || workOrder?.companyGstin || "",
        // taxRegime: formValues.taxRegime || "CGST/SGST",
        // tdsSection:
        //   deductions.find((d) => d.type === "TDS")?.tdsSection || "194C",
        // tdsRate: deductions.find((d) => d.type === "TDS")?.percentage || 0,
        // mobilizationRecovery:
        //   deductions.find((d) => d.type === "Mobilization")?.amount || 0,
        // retentionAmount:
        //   deductions.find((d) => d.type === "Retention")?.amount || 0,
        // onHoldAmount:
        //   deductions.find((d) => d.type === "On Hold" || d.type === "On-Hold")
        //     ?.amount || 0,
        notes: notes || "",
        isDraft: false,
        isSelfUpdated: true,
        approvalsRequired: approvalRequired === "true",
        approvals: approvalRequired === "true" ? approvals.data : [],
        isStrictApproval: approvalRequired === "true" ? isStrictApproval : false,
        // Additional Charges Breakdown
        additionalChargesBreakdown: additionalCharges
          .filter((charge) => charge.chargeName && charge.amount > 0)
          .map((charge) => ({
            chargeName: charge.chargeName,
            amount: charge.amount,
          })),
        // RA Bill Advance Deductions (Riskflags + Advance Items)
        raBillAdvanceDeductions: [
          // Selected Riskflags Items
          ...(workOrder?.riskflassItems || [])
            .filter((item: any) =>
              selectedRiskflassItems.has(
                item.workOrderAdvanceDeductionId?.toString() ||
                item.id?.toString() ||
                ""
              )
            )
            .map((item: any) => {
              const itemId =
                item.workOrderAdvanceDeductionId?.toString() ||
                item.id?.toString() ||
                "";
              return {
                workOrderAdvanceDeductionId:
                  item.workOrderAdvanceDeductionId || parseInt(itemId),
                amount:
                  riskflassItemAmounts[itemId] !== undefined
                    ? riskflassItemAmounts[itemId]
                    : item.totalAmount || 0,
                notes:
                  riskflassItemRemarks[itemId] !== undefined
                    ? riskflassItemRemarks[itemId]
                    : "",
              };
            }),
          // Selected Advance Items
          ...(workOrder?.advanceItems || [])
            .filter((item: any) =>
              selectedAdvanceItems.has(
                item.workOrderAdvanceDeductionId?.toString() ||
                item.id?.toString() ||
                ""
              )
            )
            .map((item: any) => {
              const itemId =
                item.workOrderAdvanceDeductionId?.toString() ||
                item.id?.toString() ||
                "";
              return {
                workOrderAdvanceDeductionId:
                  item.workOrderAdvanceDeductionId || parseInt(itemId),
                amount:
                  advanceItemAmounts[itemId] !== undefined
                    ? advanceItemAmounts[itemId]
                    : item.totalAmount || 0,
                notes:
                  advanceItemRemarks[itemId] !== undefined
                    ? advanceItemRemarks[itemId]
                    : "",
              };
            }),
        ],
        raBillLines: workLines
          .filter((line) => {
            // For stage-wise lines, check if any stage has quantity > 0
            if (line.hasStages && line.stages && line.stages.length > 0) {
              return line.stages.some((stage) => stage.thisBillQuantity > 0);
            }
            // For non-stage lines, check thisBillQuantity
            return line.thisBillQuantity > 0;
          })
          .map((line) => {
            // For stage-wise lines, include stages array
            if (line.hasStages && line.stages && line.stages.length > 0) {
              const activeStages = line.stages.filter(
                (stage) => stage.thisBillQuantity > 0
              );
              return {
                workOrderLineId: parseInt(line.id),
                remarks: line.remarks || "",
                stages: activeStages.map((stage) => ({
                  workOrderLineStageId: stage.workOrderLineStageId,
                  billedAmount: stage.thisBillAmount || 0,
                  remarks: stage.remarks || "",
                })),
              };
            }
            // For non-stage lines, use current format
            return {
              workOrderLineId: parseInt(line.id),
              billedQuantity: line.thisBillQuantity,
              remarks:
                line.remarks ||
                `${line.workLineTitle} - Billed quantity: ${line.thisBillQuantity} ${line.uom}`,
            };
          }),
        filesData: uploadedFiles.map((file, index) => ({
          order: index,
          module: "Work Order Management",
          moduleFileType: index === 0 ? "Measurement Sheet" : "Site Photos",
        })),
      };

      console.log("Submitting RA Bill data:", raBillData);

      // Create FormData for multipart request
      const formData = new FormData();
      formData.append("raBill", JSON.stringify(raBillData));

      // Append files if any
      uploadedFiles.forEach((file, index) => {
        formData.append("files", file);
      });

      const response: any = await RABillApis.createRABill(formData);

      if (response.data) {
        Swal.fire({
          icon: "success",
          title: "RA Bill Submitted!",
          text: `RA Bill has been submitted for approval successfully`,
          confirmButtonText: "OK",
        }).then(() => {
          navigate(-1);
        });
      } else {
        throw message.error(response.message);
      }
    } catch (error) {
      console.error("Error submitting RA Bill:", error);
      // message.error("Failed to submit RA Bill");
    } finally {
      setLoading(false);
    }
  };

  const recalculateTotals = (
    lines: WorkLine[],
    currentDeductions: any[],
    currentAdditionalCharges: AdditionalCharge[] = additionalCharges
  ) => {
    console.log(
      "recalculateTotals called with lines:",
      lines.length,
      "deductions:",
      currentDeductions.length
    );

    // Calculate work lines subtotal
    const workLinesSubtotal = lines.reduce(
      (sum, line) => sum + (line.lineSubtotal || 0),
      0
    );

    // Calculate additional charges total
    const additionalChargesTotal = currentAdditionalCharges.reduce(
      (sum, charge) => sum + (charge.amount || 0),
      0
    );

    // Total subtotal = work lines + additional charges
    const subtotalExclTax = workLinesSubtotal + additionalChargesTotal;

    const taxTotal = lines.reduce((sum, line) => sum + (line.taxValue || 0), 0);
    const grossTotal = subtotalExclTax + taxTotal;

    console.log("Calculated totals:", {
      subtotalExclTax,
      taxTotal,
      grossTotal,
    });

    // Calculate auto deductions with business rules
    let hasChanges = false;
    const updatedDeductions = currentDeductions.map((deduction) => {
      // Mobilization handling with recovery cap logic
      if (deduction.type === "Mobilization") {
        let calculatedAmount = 0;

        // Only calculate mobilization recovery if:
        // 1. There's a bill subtotal
        // 2. There's an advance balance remaining to be recovered
        // 3. Recovery percent rate is defined and valid (1-100)
        if (
          subtotalExclTax > 0 &&
          deduction.balanceMobilizationRecovery > 0 &&
          deduction.recoveryPercentRate > 0 &&
          deduction.recoveryPercentRate <= 100
        ) {
          const recoveryCapPercent = deduction.recoveryCapPercent || 0;
          const remainingAdvance = deduction.balanceMobilizationRecovery;

          if (recoveryCapPercent > 0 && workOrder) {
            // --- Recovery Cap Logic (3 scenarios) ---
            const woTotalValue = workOrder.woTotalValue || 0;
            const previousBilled = workOrder.previousRABillsValue || 0;
            const capLimitSubtotal = (woTotalValue * recoveryCapPercent) / 100;
            const remainingSubtotalBeforeCap = capLimitSubtotal - previousBilled;

            if (remainingSubtotalBeforeCap <= 0) {
              // Cap already exceeded → no recovery
              calculatedAmount = 0;
            } else {
              const cumulativeWithCurrent = previousBilled + subtotalExclTax;
              const willReachOrExceedCap = cumulativeWithCurrent >= capLimitSubtotal;

              if (willReachOrExceedCap) {
                // SCENARIO 1: Last bill before cap → take all remaining advance
                calculatedAmount = Math.min(
                  subtotalExclTax,
                  remainingAdvance,
                  remainingSubtotalBeforeCap
                );
              } else {
                // Check if normal rate can handle remaining advance within cap space
                const normalRecoveryPossibleInCap =
                  (remainingSubtotalBeforeCap * deduction.recoveryPercentRate) / 100;
                const needsAcceleration = remainingAdvance > normalRecoveryPossibleInCap;

                if (needsAcceleration) {
                  // SCENARIO 2: Smart acceleration
                  // Calculate minimum recovery needed to stay safe
                  const remainingWindowAfterThisBill = Math.max(
                    0,
                    remainingSubtotalBeforeCap - subtotalExclTax
                  );
                  const maxSafeAdvanceAfterThisBill =
                    (remainingWindowAfterThisBill * deduction.recoveryPercentRate) / 100;
                  const minimumRequiredRecovery = Math.max(
                    0,
                    remainingAdvance - maxSafeAdvanceAfterThisBill
                  );
                  const normalRecovery =
                    (subtotalExclTax * deduction.recoveryPercentRate) / 100;
                  const smartRecovery = Math.max(minimumRequiredRecovery, normalRecovery);
                  calculatedAmount = Math.min(
                    smartRecovery,
                    subtotalExclTax,
                    remainingAdvance,
                    remainingSubtotalBeforeCap
                  );
                } else {
                  // SCENARIO 3: Normal recovery with cap constraints
                  const normalRecovery =
                    (subtotalExclTax * deduction.recoveryPercentRate) / 100;
                  calculatedAmount = Math.min(
                    normalRecovery,
                    remainingAdvance,
                    remainingSubtotalBeforeCap
                  );
                }
              }
            }
          } else {
            // No cap set → standard calculation
            calculatedAmount =
              (subtotalExclTax * deduction.recoveryPercentRate) / 100;
            calculatedAmount = Math.min(calculatedAmount, remainingAdvance);
          }
        }
        // If any condition is not met, calculatedAmount stays 0

        if (Math.abs((deduction.amount || 0) - calculatedAmount) > 0.01) {
          hasChanges = true;
        }
        return { ...deduction, amount: calculatedAmount };
      }

      if (!deduction.isManual) {
        let calculatedAmount = 0;

        if (deduction.type === "Retention") {
          // Calculate based on pre-tax or post-tax configuration
          // Only recalculate if there's actual billing happening (subtotal > 0)
          if (subtotalExclTax > 0) {
            const base =
              deduction.taxableBase === "Post-tax"
                ? grossTotal
                : subtotalExclTax;
            calculatedAmount = (base * (deduction.percentValue || 0)) / 100;
          } else {
            // No billing yet - set to 0
            calculatedAmount = 0;
          }
        } else if (
          deduction.type === "On Hold" ||
          deduction.type === "On-Hold"
        ) {
          // Calculate based on flat or percentage
          if (deduction.calculationType === "Percentage") {
            // Only recalculate if there's actual billing happening
            if (subtotalExclTax > 0) {
              calculatedAmount =
                (subtotalExclTax * (deduction.percentValue || 0)) / 100;
            } else {
              // No billing yet - set to 0
              calculatedAmount = 0;
            }
          } else {
            // Flat amount - only use if there's actual billing
            calculatedAmount =
              subtotalExclTax > 0
                ? deduction.calculatedAmount || deduction.amount || 0
                : 0;
          }
        } else if (deduction.type === "TDS") {
          // Calculate TDS based on taxable base and threshold
          let taxableBase = 0;
          if (deduction.taxableBase === "Subtotal") {
            taxableBase = subtotalExclTax;
          } else if (deduction.taxableBase === "Gross Total") {
            taxableBase = grossTotal;
          }

          // Apply TDS only if above threshold amount
          if (taxableBase > (deduction.thresholdAmount || 0)) {
            calculatedAmount =
              (taxableBase * (deduction.percentage || 0)) / 100;
          } else {
            calculatedAmount = 0;
          }
        }

        // Check if amount actually changed
        if (Math.abs((deduction.amount || 0) - calculatedAmount) > 0.01) {
          hasChanges = true;
        }

        return { ...deduction, amount: calculatedAmount };
      }
      return deduction;
    });

    // Only update deductions if amounts have actually changed
    if (hasChanges) {
      setDeductions(updatedDeductions);
    }

    // Calculate TCS on Gross Total
    const tcsRate = workOrder?.tcsRate || 0;
    const tcsAmount =
      tcsRate && grossTotal > 0 ? (grossTotal * tcsRate) / 100 : 0;

    // Calculate TDS on Subtotal
    const tdsRate = workOrder?.tdsRate || 0;
    const tdsAmount =
      tdsRate && subtotalExclTax > 0 ? (subtotalExclTax * tdsRate) / 100 : 0;

    // Calculate Mobilization Interest
    const mobilizationDeduction = updatedDeductions.find(
      (d) => d.type === "Mobilization"
    );
    let interestAmount = 0;
    let interestDays = 0;
    let interestFromDate = "";
    let interestToDate = "";
    const interestCharged = mobilizationDeduction?.interestCharged || false;
    const interestRate = mobilizationDeduction?.interestRate || 0;

    if (
      interestCharged &&
      interestRate > 0 &&
      mobilizationDeduction?.balanceAmount > 0
    ) {
      // Use lastRaBillDate if available, otherwise use contractDate
      const fromDate = workOrder?.lastRaBillDate
        ? dayjs(workOrder.lastRaBillDate)
        : workOrder?.contractDate
          ? dayjs(workOrder.contractDate)
          : null;
      const toDate = dayjs(); // Current date

      if (fromDate && fromDate.isValid()) {
        interestDays = toDate.diff(fromDate, "day");
        interestFromDate = fromDate.format("YYYY-MM-DD");
        interestToDate = toDate.format("YYYY-MM-DD");

        if (interestDays > 0) {
          // Interest = Balance * Rate% * Days / 365
          interestAmount =
            (mobilizationDeduction.balanceAmount *
              interestRate *
              interestDays) /
            (100 * 365);
        }
      }
    }

    // Total payment deductions (Mobilization, Retention, On Hold - from deductions array)
    const totalPaymentDeductions = updatedDeductions.reduce(
      (sum, ded) => sum + (ded.amount || 0),
      0
    );

    // Total deductions = Payment Deductions + TDS
    const totalDeductions = totalPaymentDeductions + tdsAmount;

    // Net Payable = Gross Total + TCS + Interest - Total Deductions
    const netPayable =
      grossTotal + tcsAmount + interestAmount - totalDeductions;

    // Extract individual deduction amounts for the totals interface
    const mobilizationRecovery =
      updatedDeductions.find((d) => d.type === "Mobilization")?.amount || 0;
    const retention =
      updatedDeductions.find((d) => d.type === "Retention")?.amount || 0;
    const onHold =
      updatedDeductions.find(
        (d) => d.type === "On Hold" || d.type === "On-Hold"
      )?.amount || 0;
    // TDS is calculated separately, not from deductions array

    const finalTotals = {
      raBillId: raBillNumber || "DRAFT",
      subtotalExclTax,
      taxTotal,
      grossTotal,
      tcs: tcsAmount,
      mobilizationRecovery,
      retention,
      onHold,
      tds: tdsAmount,
      totalDeductions,
      roundOff: 0, // Add round-off logic if needed
      // Mobilization Interest fields
      interestCharged,
      interestAmount,
      interestRate,
      interestDays,
      interestFromDate,
      interestToDate,
      netPayable,
    };

    setTotals(finalTotals);
  };

  // Work Lines Table Columns
  const workLinesColumns: TableColumnsType<any> = useMemo(
    () => [
      // {
      //   title: "Work Line #",
      //   dataIndex: "workLineNumber",
      //   key: "workLineNumber",
      //   width: 120,
      //   fixed: "left",
      //   render: (text) => <Text strong>{text}</Text>,
      // },
      {
        title: "Work Line Title",
        dataIndex: "workLineTitle",
        key: "workLineTitle",
        width: 200,
        render: (text) => <Text strong>{text}</Text>,
      },
      {
        title: "Description",
        key: "description",
        width: 200,
        render: (_, record) => (
          <div className="text-muted small">{record.description}</div>
        ),
      },
      {
        title: "UOM",
        dataIndex: "uom",
        key: "uom",
        width: 80,
        align: "center",
      },
      {
        title: "Contract Qty",
        dataIndex: "contractQuantity",
        key: "contractQuantity",
        width: 120,
        align: "right",
        render: (qty) => qty.toLocaleString("en-IN"),
      },
      {
        title: "Cum Approved Qty",
        dataIndex: "cumulativeApprovedQuantity",
        key: "cumulativeApprovedQuantity",
        width: 130,
        align: "right",
        render: (qty) => (
          <Text type="success" strong>
            {qty.toLocaleString("en-IN")}
          </Text>
        ),
      },
      {
        title: "Prev Billed Qty",
        dataIndex: "previousBilledQuantity",
        key: "previousBilledQuantity",
        width: 120,
        align: "right",
        render: (qty) => <Text type="secondary">{qty.toLocaleString("en-IN")}</Text>,
      },
      {
        title: "Pending Approval Qty",
        dataIndex: "pendingApprovalQuantity",
        key: "pendingApprovalQuantity",
        width: 150,
        align: "right",
        render: (qty) => <Text type="warning">{qty.toLocaleString("en-IN")}</Text>,
      },
      {
        title: "On Hold Qty",
        dataIndex: "onHoldQuantity",
        key: "onHoldQuantity",
        width: 120,
        align: "right",
        render: (qty) => <Text type="danger">{qty.toLocaleString("en-IN")}</Text>,
      },
      {
        title: "Available to Bill",
        dataIndex: "availableQuantity",
        key: "availableQuantity",
        width: 120,
        align: "right",
        render: (qty) => (
          <Text strong style={{ color: "#1890ff" }}>
            {qty.toLocaleString("en-IN")}
          </Text>
        ),
      },
      {
        title: "This Bill Qty/Amt",
        key: "thisBillQuantity",
        width: 150,
        align: "center",
        render: (_, record) => {
          // For stage-wise lines, show tag with total amount
          if (record.hasStages && record.stages && record.stages.length > 0) {
            const totalStageAmount = record.stages.reduce(
              (sum: number, stage: WorkLineStage) =>
                sum + (stage.thisBillAmount || 0),
              0
            );
            return (
              <div style={{ textAlign: "center" }}>
                <Tag color="purple" style={{ marginBottom: 4 }}>
                  <ScheduleOutlined /> Stage-wise
                </Tag>
                <div>
                  <Text
                    strong
                    style={{
                      color: totalStageAmount > 0 ? "#52c41a" : undefined,
                    }}
                  >
                    ₹
                    {totalStageAmount.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                  </Text>
                </div>
              </div>
            );
          }
          // For non-stage lines, show quantity input
          return (
            <div>
              <InputNumber
                min={0}
                precision={2}
                value={record.thisBillQuantity}
                onChange={(value) => handleQuantityChange(value, record.id)}
                style={{ width: "100%" }}
                placeholder="Enter quantity"
                status={record.error ? "error" : undefined}
              />
              {record.error && (
                <div
                  style={{
                    color: "#ff4d4f",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {record.error}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: "Rate",
        dataIndex: "rate",
        key: "rate",
        width: 100,
        align: "right",
        render: (rate) => `₹${rate.toLocaleString("en-IN")}`,
      },
      {
        title: "Line Subtotal",
        dataIndex: "lineSubtotal",
        key: "lineSubtotal",
        width: 130,
        align: "right",
        render: (amount) => <Text strong>{formatCurrency(amount, "INR")}</Text>,
      },
      {
        title: "Tax",
        key: "tax",
        width: 180,
        render: (_: any, record: any) => {
          console.log("record", record);
          return (
            <div>
              {record.taxTypeName && (
                <div className="small">{record.taxTypeName}</div>
              )}
              {(record.sgstRate > 0 ||
                record.cgstRate > 0 ||
                record.igstRate > 0) && (
                  <div className="small text-muted">
                    {record.sgstRate > 0 && record.cgstRate > 0 ? (
                      <>
                        SGST: {record.sgstRate}% + CGST: {record.cgstRate}%
                      </>
                    ) : record.igstRate > 0 ? (
                      <>IGST: {record.igstRate}%</>
                    ) : (
                      <>{record.taxPercentage}%</>
                    )}
                  </div>
                )}
              <div className="fw-bold">
                ₹{record.taxValue?.toLocaleString("en-IN") || 0}
              </div>
            </div>
          );
        },
      },
      // {
      //   title: "GST Value",
      //   dataIndex: "gstValue",
      //   key: "gstValue",
      //   width: 120,
      //   align: "right",
      //   render: (amount) => (
      //     <Text type="warning">{formatCurrency(amount, "INR")}</Text>
      //   ),
      // },
      {
        title: "Line Total",
        dataIndex: "lineTotal",
        key: "lineTotal",
        width: 130,
        align: "right",
        render: (amount) => (
          <Text strong style={{ color: "#52c41a" }}>
            {formatCurrency(amount, "INR")}
          </Text>
        ),
      },
    ],
    [workLines]
  );

  if (loading && !workOrder) {
    return (
      <Content>
        <div style={{ textAlign: "center", marginTop: "20vh" }}>
          <Spin size="large" />
          <div className="mt-3">Loading work order details...</div>
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <Head title="Create RA Bill" />

      {/* Header */}
      <div className="nk-content-inner">
        <div className="nk-content-body">
          <div className="nk-block-head nk-block-head-sm">
            <div className="nk-block-between g-3">
              <div className="nk-block-head-content">
                <Title level={3} className="nk-block-title">
                  Create RA Bill
                </Title>
                <div className="nk-block-des text-soft">
                  <p>
                    Work Order: {workOrder?.woNumber} |{" "}
                    {isClientFlow ? "Client" : "Contractor"}:{" "}
                    {workOrder?.contractor}
                  </p>
                </div>
              </div>
              <div className="nk-block-head-content">
                <Space>
                  <Button
                    className="toggle"
                    color="primary"
                    onClick={() => {
                      const searchParams = new URLSearchParams(location.search);
                      const flowType = searchParams.get("flowType");
                      const flowTypeQuery = flowType
                        ? `?flowType=${flowType}`
                        : "";
                      navigate(
                        `${process.env.PUBLIC_URL}/ra-bills-create${flowTypeQuery}`
                      );
                    }}
                    style={{ marginRight: 16 }}
                  >
                    <Icon name="arrow-left" />
                    Back
                  </Button>
                  {/* <Button icon={<EyeOutlined />}>Preview PDF</Button>
                  <Button icon={<DownloadOutlined />}>Export Lines</Button> */}
                </Space>
              </div>
            </div>
          </div>

          {/* Validation Errors */}
          {showValidation && validationErrors.length > 0 && (
            <Alert
              type="error"
              showIcon
              closable
              message="Validation Errors"
              description={
                <ul style={{ marginBottom: 0 }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              }
              onClose={() => setShowValidation(false)}
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Header Details */}
          <Card title="RA Bill Header" className="mb-4">
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                {/* {(sequenceAccess?.level === "Site" &&
                  !sequenceAccess?.siteIds?.includes(
                    workOrder?.site?.toString()
                  )) ||
                  sequenceAccess?.module === "" ? (
                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="RA Bill Number"
                      name="raBillNumber"
                      rules={[
                        {
                          required: true,
                          message: "Please enter RA Bill Number",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter RA Bill Number"
                        value={raBillNumber}
                        onChange={(e) => setRABillNumber(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                ) : null} */}

                <Col xs={24} lg={8}>
                  <div style={{ position: 'relative' }}>
                    <Form.Item
                      label="RA Bill Number"
                      name="raBillNumber"
                      rules={[
                        {
                          required: numberingMode === 'manual',
                          message: "Please enter RA Bill Number",
                        },
                      ]}
                    >
                      <Input
                        placeholder={numberingMode === 'auto' ? 'Auto-generated' : 'Enter RA Bill Number'}
                        value={raBillNumber}
                        onChange={(e) => setRABillNumber(e.target.value)}
                        disabled={numberingMode === 'auto'}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>

                    <Tooltip title="Click here to enable or disable auto-generation of RA Bill numbers.">
                      <SettingOutlined
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '55px',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          fontSize: '18px',
                          color: '#1890ff',
                          zIndex: 1,
                        }}
                        onClick={() => {
                          setTempNumberingMode(numberingMode);
                          setShowNumberingModal(true);
                        }}
                      />
                    </Tooltip>
                  </div>
                </Col>

                <Col xs={24} lg={8}>
                  <Form.Item label="Work Order Number" name="woNumber">
                    <Input value={workOrder?.woNumber} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item
                    label={isClientFlow ? "Client" : "Contractor"}
                    name="contractor"
                  >
                    <Input disabled={!!workOrder?.contractor} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item
                    label="RA Period From"
                    name="raPeriodFrom"
                    rules={[{ required: true }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      minDate={workOrder?.plannedStartDate}
                      maxDate={dayjs(new Date())}
                      format="DD-MMM-YYYY"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item
                    label="RA Period To"
                    name="raPeriodTo"
                    rules={[{ required: true }]}
                  >
                    <DatePicker
                      minDate={workOrder?.plannedStartDate}
                      maxDate={dayjs(new Date())}
                      style={{ width: "100%" }}
                      format="DD-MMM-YYYY"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item
                    label="Payment Due Date"
                    name="paymentDueDate"
                    rules={[{ required: true }]}
                  >
                    <DatePicker
                      minDate={dayjs()}
                      style={{ width: "100%" }}
                      format="DD-MM-YYYY"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item label="Place of Supply" name="placeOfSupply">
                    <Select
                      defaultValue={workOrder?.placeOfSupply}
                      placeholder="Select place of supply"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input: any, option: any) =>
                        option?.children
                          ?.toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {statesList.map((state) => (
                        <Option key={state.id} value={state.stateName}>
                          {state.stateName}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item
                    label={isClientFlow ? "Client GSTIN" : "Contractor GSTIN"}
                    name="contractorGSTIN"
                    rules={[{ required: false }]}
                  >
                    <Input disabled={!!workOrder?.contractorGSTIN} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item label="Company GSTIN" name="companyGstin">
                    <Input disabled={!!workOrder?.companyGstin} />
                  </Form.Item>
                </Col>
                {/* <Col xs={24} lg={8}>
                  <Form.Item label="Tax Regime" name="taxRegime">
                    <Select defaultValue="CGST/SGST">
                      <Option value="Cgst Sgst">CGST/SGST</Option>
                      <Option value="Igst">IGST</Option>
                    </Select>
                  </Form.Item>
                </Col> */}
                <Col xs={24} lg={8}>
                  <Form.Item
                    label="Approval Required?"
                    required
                    style={{ marginBottom: 0 }}
                  >
                    <Radio.Group
                      value={approvalRequired}
                      onChange={(e) => setApprovalRequired(e.target.value)}
                      style={{ width: "100%" }}
                    >
                      <Radio.Button value="true">Yes</Radio.Button>
                      <Radio.Button value="false">No</Radio.Button>
                    </Radio.Group>

                    {/* Set Approvals Link */}
                    {approvalRequired === "true" && (
                      <div style={{ marginTop: 8 }}>
                        <Popover
                          content="Click here to set required approvals"
                          title="Approval Required"
                        >
                          <a
                            href="#"
                            className="success"
                            onClick={(e) => {
                              e.preventDefault();
                              setApprovals((pre) => ({
                                ...pre,
                                open: true,
                              }));
                            }}
                          >
                            <EditOutlined /> Set Approvals &nbsp;
                            {approvals?.data?.length === 0 ? (
                              <span className="mandatory">
                                Please set approvals
                              </span>
                            ) : (
                              <span className="text-success">
                                ✓ {approvals.data.length} approval(s) configured
                              </span>
                            )}
                          </a>
                        </Popover>
                      </div>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* Commercial Summary */}
            <Divider>Commercial Summary</Divider>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">WO Total Value</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(workOrder?.woTotalValue || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Cumulative Work Done %</div>
                  <div className="fw-bold h5 mb-0 text-success">
                    {workOrder?.cumulativeWorkDone || 0}%
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Cumulative Work Value</div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(workOrder?.cumulativeWorkValue || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={12} lg={5}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">
                    Previous RA Bills Value
                  </div>
                  <div className="fw-bold h5 mb-0 text-info">
                    {formatCurrency(
                      workOrder?.previousRABillsValue || 0,
                      "INR"
                    )}
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={12} lg={5}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">Current Bill Value</div>
                  <div className="fw-bold h5 mb-0 text-primary">
                    {formatCurrency(totals?.subtotalExclTax || 0, "INR")}
                  </div>
                </div>
              </Col>
              {/* <Col xs={12} lg={4}>
                <div className="text-center p-3 border rounded">
                  <div className="text-muted small">
                    Balance Works for RA Bill
                  </div>
                  <div className="fw-bold h5 mb-0">
                    {formatCurrency(
                      (workOrder?.cumulativeWorkValue || 0) -
                        (workOrder?.previousRABillsValue || 0) -
                        (totals?.netPayable || 0),
                      "INR"
                    )}
                  </div>
                </div>
              </Col> */}
            </Row>
          </Card>

          {/* Work Lines */}
          <Card title="Work Lines" className="mb-4">
            <Table
              columns={workLinesColumns}
              dataSource={workLines}
              rowKey="id"
              scroll={{ x: 1600 }}
              pagination={false}
              size="small"
              expandable={{
                expandedRowRender: (record: WorkLine) => {
                  // Get current line data from state to ensure we have latest values
                  const currentLine = workLines.find((l) => l.id === record.id);
                  const stages = currentLine?.stages || record.stages;

                  if (!stages || stages.length === 0) return null;

                  return (
                    <div
                      style={{
                        padding: "12px 16px",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <div
                        style={{
                          marginBottom: 12,
                          fontWeight: 600,
                          color: "#722ed1",
                        }}
                      >
                        <ScheduleOutlined style={{ marginRight: 8 }} />
                        Stage-wise Billing ({stages.length} stages)
                      </div>
                      <Table
                        dataSource={stages.map((stage, idx) => ({
                          ...stage,
                          key: stage.workOrderLineStageId || idx,
                          stageAmount:
                            (record.rate * (stage.splitPercentage || 0)) / 100,
                        }))}
                        columns={[
                          {
                            title: "Stage",
                            dataIndex: "stageTitle",
                            key: "stageTitle",
                            width: 180,
                          },
                          {
                            title: "Percentage",
                            dataIndex: "splitPercentage",
                            key: "splitPercentage",
                            width: 100,
                            render: (val: number) => (
                              <Tag color="blue">{val}%</Tag>
                            ),
                          },
                          {
                            title: "Stage Rate",
                            dataIndex: "stageAmount",
                            key: "stageAmount",
                            width: 150,
                            render: (val: number) => (
                              <span style={{ fontWeight: 500 }}>
                                ₹
                                {val?.toLocaleString("en-IN", {
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            ),
                          },
                          {
                            title: "Approved Qty",
                            dataIndex: "approvedQty",
                            key: "approvedQty",
                            width: 150,
                          },
                          {
                            title: "Prev Billed Qty",
                            dataIndex: "cumulativeBilledQty",
                            key: "cumulativeBilledQty",
                            width: 150,
                          },
                          {
                            title: "Pending Approval Qty",
                            dataIndex: "pendingApprovalQty",
                            key: "pendingApprovalQty",
                            width: 150,
                          },
                          {
                            title: "On Hold Qty",
                            dataIndex: "onHoldQty",
                            key: "onHoldQty",
                            width: 150,
                          },
                          {
                            title: "Available to Bill Qty",
                            dataIndex: "AvailabletoBillQty",
                            key: "AvailabletoBillQty",
                            width: 150,
                          },
                          {
                            title: "This Bill Qty",
                            key: "thisBillQuantity",
                            width: 120,
                            render: (_: any, stage: any) => (
                              <div>
                                <InputNumber
                                  min={0}
                                  value={stage.thisBillQuantity}
                                  onChange={(value) =>
                                    handleStageBillQuantityChange(
                                      currentLine?.id || record.id,
                                      stage.workOrderLineStageId,
                                      value,
                                      stage.stageRate || 0
                                    )
                                  }
                                  style={{ width: "100%" }}
                                  placeholder="Enter qty"
                                  status={stage.error ? "error" : undefined}
                                />
                                {stage.error && (
                                  <div
                                    style={{
                                      color: "#ff4d4f",
                                      fontSize: "12px",
                                      marginTop: "4px",
                                    }}
                                  >
                                    {stage.error}
                                  </div>
                                )}
                              </div>
                            ),
                          },
                          {
                            title: "This Bill Amount",
                            key: "thisBillAmount",
                            width: 150,
                            render: (_: any, stage: WorkLineStage) => (
                              <span style={{ fontWeight: 500 }}>
                                ₹
                                {(stage.thisBillAmount || 0).toLocaleString(
                                  "en-IN",
                                  {
                                    maximumFractionDigits: 2,
                                  }
                                )}
                              </span>
                            ),
                          },
                          {
                            title: "Remarks",
                            key: "remarks",
                            width: 200,
                            render: (_: any, stage: WorkLineStage) => (
                              <Input
                                value={stage.remarks}
                                onChange={(e) =>
                                  handleStageRemarksChange(
                                    currentLine?.id || record.id,
                                    stage.workOrderLineStageId,
                                    e.target.value
                                  )
                                }
                                placeholder="Enter remarks"
                                style={{ width: "100%" }}
                              />
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                        bordered
                        summary={() => {
                          const totalThisBillQty = stages.reduce(
                            (sum, stage) => sum + (stage.thisBillQuantity || 0),
                            0
                          );
                          const totalThisBillAmount = stages.reduce(
                            (sum, stage) => sum + (stage.thisBillAmount || 0),
                            0
                          );
                          return (
                            <Table.Summary fixed>
                              <Table.Summary.Row
                                style={{ backgroundColor: "#e6f7ff" }}
                              >
                                {/* Stage */}
                                <Table.Summary.Cell index={0}>
                                  <strong>Total</strong>
                                </Table.Summary.Cell>
                                {/* Percentage */}
                                <Table.Summary.Cell index={1}>
                                  -
                                </Table.Summary.Cell>
                                {/* Stage Rate */}
                                <Table.Summary.Cell index={2}>
                                  -
                                </Table.Summary.Cell>
                                {/* Approved Qty */}
                                <Table.Summary.Cell index={3}>
                                  -
                                </Table.Summary.Cell>
                                {/* Prev Billed Qty */}
                                <Table.Summary.Cell index={4}>
                                  -
                                </Table.Summary.Cell>
                                {/* Pending Approval Qty */}
                                <Table.Summary.Cell index={5}>
                                  -
                                </Table.Summary.Cell>
                                {/* On Hold Qty */}
                                <Table.Summary.Cell index={6}>
                                  -
                                </Table.Summary.Cell>
                                {/* Available to Bill Qty */}
                                <Table.Summary.Cell index={7}>
                                  -
                                </Table.Summary.Cell>
                                {/* This Bill Qty */}
                                <Table.Summary.Cell index={8}>
                                  <strong style={{ color: "#1890ff" }}>
                                    {totalThisBillQty.toLocaleString("en-IN", {
                                      maximumFractionDigits: 2,
                                    })}
                                  </strong>
                                </Table.Summary.Cell>
                                {/* This Bill Amount */}
                                <Table.Summary.Cell index={9}>
                                  <strong style={{ color: "#52c41a" }}>
                                    ₹
                                    {totalThisBillAmount.toLocaleString(
                                      "en-IN",
                                      {
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </strong>
                                </Table.Summary.Cell>
                                {/* Remarks */}
                                <Table.Summary.Cell index={10}>
                                  -
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            </Table.Summary>
                          );
                        }}
                      />
                    </div>
                  );
                },
                expandIcon: ({ expanded, onExpand, record }: any) => {
                  const hasStages = record.stages && record.stages.length > 0;
                  if (!hasStages) return null;

                  return expanded ? (
                    <DownOutlined
                      onClick={(e) => onExpand(record, e)}
                      style={{
                        color: "#722ed1",
                        cursor: "pointer",
                      }}
                    />
                  ) : (
                    <RightOutlined
                      onClick={(e) => onExpand(record, e)}
                      style={{
                        color: "#722ed1",
                        cursor: "pointer",
                      }}
                    />
                  );
                },
                rowExpandable: (record: WorkLine) =>
                  !!(
                    record.hasStages &&
                    record.stages &&
                    record.stages.length > 0
                  ),
              }}
            />

            {/* Riskflass & Advance Items */}
            <div className="mt-3">
              {/* Riskflags Items - Top */}
              <div
                className="p-3 border rounded bg-light mb-3"
                style={{ width: "100%" }}
              >
                <div className="fw-bold text-primary mb-2">Riskflags</div>
                {workOrder?.riskflassItems &&
                  workOrder.riskflassItems.length > 0 ? (
                  <div>
                    <Table
                      dataSource={workOrder.riskflassItems}
                      rowKey={(record, index) =>
                        record.workOrderAdvanceDeductionId?.toString() ||
                        record.id?.toString() ||
                        index?.toString() ||
                        ""
                      }
                      pagination={false}
                      bordered
                      size="small"
                      columns={[
                        {
                          title: "Select",
                          key: "select",
                          width: 80,
                          align: "center",
                          render: (_: any, record: any) => (
                            <Checkbox
                              checked={selectedRiskflassItems.has(
                                record.workOrderAdvanceDeductionId?.toString() ||
                                record.id?.toString() ||
                                ""
                              )}
                              onChange={(e) => {
                                const newSelected = new Set(
                                  selectedRiskflassItems
                                );
                                const itemId =
                                  record.workOrderAdvanceDeductionId?.toString() ||
                                  record.id?.toString() ||
                                  "";
                                if (e.target.checked) {
                                  newSelected.add(itemId);
                                } else {
                                  newSelected.delete(itemId);
                                }
                                setSelectedRiskflassItems(newSelected);
                              }}
                            />
                          ),
                        },
                        {
                          title: "S.No",
                          key: "sno",
                          width: 60,
                          align: "center",
                          render: (_: any, __: any, index: number) => index + 1,
                        },
                        {
                          title: "Name",
                          dataIndex: "name",
                          key: "name",
                          width: 250,
                          render: (name: string) => {
                            if (!name) return "-";
                            const maxLength = 30;
                            const isLong = name.length > maxLength;
                            const displayText = isLong
                              ? name.substring(0, maxLength) + "..."
                              : name;
                            return (
                              <Tooltip
                                title={isLong ? name : null}
                                placement="topLeft"
                              >
                                <div
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    cursor: isLong ? "pointer" : "default",
                                  }}
                                >
                                  {displayText}
                                </div>
                              </Tooltip>
                            );
                          },
                        },
                        {
                          title: "Is Chargeable",
                          dataIndex: "isChargable",
                          key: "isChargable",
                          width: 120,
                          align: "center",
                          render: (isChargable: boolean) => (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                              }}
                            >
                              <Checkbox
                                checked={isChargable}
                                disabled
                                className="custom-checkbox-white"
                              />
                              <span>Yes</span>
                            </div>
                          ),
                        },
                        {
                          title: "Total",
                          dataIndex: "totalAmount",
                          key: "totalAmount",
                          width: 200,
                          align: "right",
                          render: (amount: number) => (
                            <div style={{ textAlign: "right" }}>
                              <span style={{ fontWeight: 500 }}>
                                ₹
                                {(amount || 0).toLocaleString("en-IN", {
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          ),
                        },
                        {
                          title: "Cumulative Recovered Amount",
                          key: "cumulativeRecoveredAmount",
                          width: 250,
                          align: "right",
                          render: (_: any, record: any) => {
                            const totalRecovered =
                              record.totalRecoveredAmount || 0;
                            const totalPending =
                              record.totalPendingRecoveredAmount || 0;
                            const cumulativeAmount =
                              totalRecovered + totalPending;
                            if (cumulativeAmount > 0) {
                              return (
                                <div style={{ textAlign: "right" }}>
                                  <span style={{ fontWeight: 500 }}>
                                    ₹
                                    {cumulativeAmount.toLocaleString("en-IN", {
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </div>
                              );
                            }
                            return <span style={{ color: "#999" }}>-</span>;
                          },
                        },
                        {
                          title: "Amount",
                          key: "amount",
                          width: 150,
                          align: "right",
                          render: (_: any, record: any) => {
                            const itemId =
                              record.workOrderAdvanceDeductionId?.toString() ||
                              record.id?.toString() ||
                              "";

                            // Check if item is selected
                            const isSelected =
                              selectedRiskflassItems.has(itemId);

                            // Calculate balance amount (initial value)
                            const totalAmount = record.totalAmount || 0;
                            const totalRecovered =
                              record.totalRecoveredAmount || 0;
                            const totalPending =
                              record.totalPendingRecoveredAmount || 0;
                            const cumulativeRecoveredAmount =
                              totalRecovered + totalPending;
                            const balanceAmount =
                              totalAmount - cumulativeRecoveredAmount;

                            // Get current amount from state, or use balanceAmount as initial
                            const currentAmount =
                              riskflassItemAmounts[itemId] !== undefined
                                ? riskflassItemAmounts[itemId]
                                : balanceAmount;

                            return (
                              <div>
                                <InputNumber
                                  style={{ width: "100%" }}
                                  value={currentAmount}
                                  disabled={!isSelected}
                                  onChange={(value) => {
                                    const newAmount = value || 0;

                                    // Skip validation if we're auto-correcting
                                    if (isAutoCorrectingRef.current[itemId]) {
                                      delete isAutoCorrectingRef.current[
                                        itemId
                                      ];
                                      setRiskflassItemAmounts((prev) => ({
                                        ...prev,
                                        [itemId]: newAmount,
                                      }));
                                      return;
                                    }

                                    // Always update the value (even if invalid) so onBlur can correct it
                                    setRiskflassItemAmounts((prev) => ({
                                      ...prev,
                                      [itemId]: newAmount,
                                    }));

                                    // Validate: amount should not exceed balance amount
                                    if (
                                      balanceAmount > 0 &&
                                      newAmount > balanceAmount
                                    ) {
                                      message.error(
                                        `Amount cannot exceed balance amount of ₹${balanceAmount.toLocaleString(
                                          "en-IN",
                                          {
                                            maximumFractionDigits: 2,
                                          }
                                        )}`
                                      );
                                    }
                                  }}
                                  onBlur={() => {
                                    // Get current amount from state
                                    const currentAmountValue =
                                      riskflassItemAmounts[itemId] !== undefined
                                        ? riskflassItemAmounts[itemId]
                                        : balanceAmount;

                                    // Auto-correct on blur: cap at balance amount if exceeds
                                    if (
                                      balanceAmount > 0 &&
                                      currentAmountValue > balanceAmount
                                    ) {
                                      // Set flag before updating value to prevent error message
                                      isAutoCorrectingRef.current[itemId] =
                                        true;
                                      setRiskflassItemAmounts((prev) => ({
                                        ...prev,
                                        [itemId]: balanceAmount,
                                      }));
                                      // Clear flag after a small delay to ensure onChange has processed
                                      setTimeout(() => {
                                        delete isAutoCorrectingRef.current[
                                          itemId
                                        ];
                                      }, 100);
                                    }
                                  }}
                                  min={0}
                                  precision={2}
                                  controls={false}
                                  formatter={indianCurrencyFormatter}
                                  parser={indianCurrencyParser}
                                  placeholder="Enter amount"
                                  onKeyPress={(e) => {
                                    if (
                                      !/[0-9.]/.test(e.key) &&
                                      ![
                                        "Backspace",
                                        "Delete",
                                        "Tab",
                                        "Escape",
                                        "Enter",
                                      ].includes(e.key)
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </div>
                            );
                          },
                        },
                        {
                          title: "Remarks",
                          dataIndex: "notes",
                          key: "notes",
                          width: 300,
                          render: (remarks: string, record: any) => {
                            const itemId =
                              record.workOrderAdvanceDeductionId?.toString() ||
                              record.id?.toString() ||
                              "";

                            // Check if item is selected
                            const isSelected =
                              selectedRiskflassItems.has(itemId);

                            const currentRemarks =
                              riskflassItemRemarks[itemId] !== undefined
                                ? riskflassItemRemarks[itemId]
                                : "";
                            const maxLength = 30;
                            const isLong = currentRemarks.length > maxLength;

                            return (
                              <Tooltip
                                title={isLong ? currentRemarks : null}
                                placement="topLeft"
                              >
                                <Input
                                  value={currentRemarks}
                                  onChange={(e) => {
                                    setRiskflassItemRemarks((prev) => ({
                                      ...prev,
                                      [itemId]: e.target.value,
                                    }));
                                  }}
                                  placeholder="Enter remarks"
                                  disabled={!isSelected}
                                />
                              </Tooltip>
                            );
                          },
                        },
                      ]}
                    />
                    <div className="mt-3 pt-2 border-top">
                      <div className="d-flex justify-content-between">
                        <span className="fw-bold">
                          Selected Riskflags Amount:
                        </span>
                        <strong className="text-warning">
                          ₹
                          {workOrder.riskflassItems
                            .filter((item: any) =>
                              selectedRiskflassItems.has(
                                item.workOrderAdvanceDeductionId?.toString() ||
                                item.id?.toString() ||
                                ""
                              )
                            )
                            .reduce((sum: number, item: any) => {
                              const itemId =
                                item.workOrderAdvanceDeductionId?.toString() ||
                                item.id?.toString() ||
                                "";
                              const amount =
                                riskflassItemAmounts[itemId] !== undefined
                                  ? riskflassItemAmounts[itemId]
                                  : item.totalAmount || 0;
                              return sum + amount;
                            }, 0)
                            .toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                        </strong>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-muted small text-center p-3">
                    No riskflags items added. Click "Add Riskflags" from the
                    actions menu to add items.
                  </div>
                )}
              </div>

              {/* Advance Items - Bottom */}
              <div
                className="p-3 border rounded bg-light"
                style={{ width: "100%" }}
              >
                <div className="fw-bold text-primary mb-2">
                  Additional Advances
                </div>
                {workOrder?.advanceItems &&
                  workOrder.advanceItems.length > 0 ? (
                  <div>
                    <Table
                      dataSource={workOrder.advanceItems}
                      rowKey={(record, index) =>
                        record.workOrderAdvanceDeductionId?.toString() ||
                        record.id?.toString() ||
                        index?.toString() ||
                        ""
                      }
                      pagination={false}
                      bordered
                      size="small"
                      columns={[
                        {
                          title: "Select",
                          key: "select",
                          width: 80,
                          align: "center",
                          render: (_: any, record: any) => (
                            <Checkbox
                              checked={selectedAdvanceItems.has(
                                record.workOrderAdvanceDeductionId?.toString() ||
                                record.id?.toString() ||
                                ""
                              )}
                              onChange={(e) => {
                                const newSelected = new Set(
                                  selectedAdvanceItems
                                );
                                const itemId =
                                  record.workOrderAdvanceDeductionId?.toString() ||
                                  record.id?.toString() ||
                                  "";
                                if (e.target.checked) {
                                  newSelected.add(itemId);
                                } else {
                                  newSelected.delete(itemId);
                                }
                                setSelectedAdvanceItems(newSelected);
                              }}
                            />
                          ),
                        },
                        {
                          title: "S.No",
                          key: "sno",
                          width: 60,
                          align: "center",
                          render: (_: any, __: any, index: number) => index + 1,
                        },
                        {
                          title: "Name",
                          dataIndex: "name",
                          key: "name",
                          width: 250,
                          render: (name: string) => {
                            if (!name) return "-";
                            const maxLength = 30;
                            const isLong = name.length > maxLength;
                            const displayText = isLong
                              ? name.substring(0, maxLength) + "..."
                              : name;
                            return (
                              <Tooltip
                                title={isLong ? name : null}
                                placement="topLeft"
                              >
                                <div
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    cursor: isLong ? "pointer" : "default",
                                  }}
                                >
                                  {displayText}
                                </div>
                              </Tooltip>
                            );
                          },
                        },
                        {
                          title: "Is Chargeable",
                          dataIndex: "isChargable",
                          key: "isChargable",
                          width: 120,
                          align: "center",
                          render: (isChargable: boolean) => (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                              }}
                            >
                              <Checkbox
                                checked={isChargable}
                                disabled
                                className="custom-checkbox-white"
                              />
                              <span>Yes</span>
                            </div>
                          ),
                        },
                        {
                          title: "Total",
                          dataIndex: "totalAmount",
                          key: "totalAmount",
                          width: 200,
                          align: "right",
                          render: (amount: number) => (
                            <div style={{ textAlign: "right" }}>
                              <span style={{ fontWeight: 500 }}>
                                ₹
                                {(amount || 0).toLocaleString("en-IN", {
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          ),
                        },
                        {
                          title: "Cumulative Recovered Amount",
                          key: "cumulativeRecoveredAmount",
                          width: 250,
                          align: "right",
                          render: (_: any, record: any) => {
                            const totalRecovered =
                              record.totalRecoveredAmount || 0;
                            const totalPending =
                              record.totalPendingRecoveredAmount || 0;
                            const cumulativeAmount =
                              totalRecovered + totalPending;
                            if (cumulativeAmount > 0) {
                              return (
                                <div style={{ textAlign: "right" }}>
                                  <span style={{ fontWeight: 500 }}>
                                    ₹
                                    {cumulativeAmount.toLocaleString("en-IN", {
                                      maximumFractionDigits: 2,
                                    })}
                                  </span>
                                </div>
                              );
                            }
                            return <span style={{ color: "#999" }}>-</span>;
                          },
                        },
                        {
                          title: "Amount",
                          key: "amount",
                          width: 150,
                          align: "right",
                          render: (_: any, record: any) => {
                            const itemId =
                              record.workOrderAdvanceDeductionId?.toString() ||
                              record.id?.toString() ||
                              "";

                            // Check if item is selected
                            const isSelected = selectedAdvanceItems.has(itemId);

                            // Calculate balance amount (initial value)
                            const totalAmount = record.totalAmount || 0;
                            const totalRecovered =
                              record.totalRecoveredAmount || 0;
                            const totalPending =
                              record.totalPendingRecoveredAmount || 0;
                            const cumulativeRecoveredAmount =
                              totalRecovered + totalPending;
                            const balanceAmount =
                              totalAmount - cumulativeRecoveredAmount;

                            // Get current amount from state, or use balanceAmount as initial
                            const currentAmount =
                              advanceItemAmounts[itemId] !== undefined
                                ? advanceItemAmounts[itemId]
                                : balanceAmount;

                            return (
                              <div>
                                <InputNumber
                                  style={{ width: "100%" }}
                                  value={currentAmount}
                                  disabled={!isSelected}
                                  onChange={(value) => {
                                    const newAmount = value || 0;

                                    // Skip validation if we're auto-correcting
                                    if (isAutoCorrectingRef.current[itemId]) {
                                      delete isAutoCorrectingRef.current[
                                        itemId
                                      ];
                                      setAdvanceItemAmounts((prev) => ({
                                        ...prev,
                                        [itemId]: newAmount,
                                      }));
                                      return;
                                    }

                                    // Always update the value (even if invalid) so onBlur can correct it
                                    setAdvanceItemAmounts((prev) => ({
                                      ...prev,
                                      [itemId]: newAmount,
                                    }));

                                    // Validate: amount should not exceed balance amount
                                    if (
                                      balanceAmount > 0 &&
                                      newAmount > balanceAmount
                                    ) {
                                      message.error(
                                        `Amount cannot exceed balance amount of ₹${balanceAmount.toLocaleString(
                                          "en-IN",
                                          {
                                            maximumFractionDigits: 2,
                                          }
                                        )}`
                                      );
                                    }
                                  }}
                                  onBlur={() => {
                                    // Get current amount from state
                                    const currentAmountValue =
                                      advanceItemAmounts[itemId] !== undefined
                                        ? advanceItemAmounts[itemId]
                                        : balanceAmount;

                                    // Auto-correct on blur: cap at balance amount if exceeds
                                    if (
                                      balanceAmount > 0 &&
                                      currentAmountValue > balanceAmount
                                    ) {
                                      // Set flag before updating value to prevent error message
                                      isAutoCorrectingRef.current[itemId] =
                                        true;
                                      setAdvanceItemAmounts((prev) => ({
                                        ...prev,
                                        [itemId]: balanceAmount,
                                      }));
                                      // Clear flag after a small delay to ensure onChange has processed
                                      setTimeout(() => {
                                        delete isAutoCorrectingRef.current[
                                          itemId
                                        ];
                                      }, 100);
                                    }
                                  }}
                                  min={0}
                                  precision={2}
                                  controls={false}
                                  formatter={indianCurrencyFormatter}
                                  parser={indianCurrencyParser}
                                  placeholder="Enter amount"
                                  onKeyPress={(e) => {
                                    if (
                                      !/[0-9.]/.test(e.key) &&
                                      ![
                                        "Backspace",
                                        "Delete",
                                        "Tab",
                                        "Escape",
                                        "Enter",
                                      ].includes(e.key)
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </div>
                            );
                          },
                        },
                        {
                          title: "Remarks",
                          dataIndex: "notes",
                          key: "notes",
                          width: 300,
                          render: (remarks: string, record: any) => {
                            const itemId =
                              record.workOrderAdvanceDeductionId?.toString() ||
                              record.id?.toString() ||
                              "";

                            // Check if item is selected
                            const isSelected = selectedAdvanceItems.has(itemId);

                            const currentRemarks =
                              advanceItemRemarks[itemId] !== undefined
                                ? advanceItemRemarks[itemId]
                                : "";
                            const maxLength = 30;
                            const isLong = currentRemarks.length > maxLength;

                            return (
                              <Tooltip
                                title={isLong ? currentRemarks : null}
                                placement="topLeft"
                              >
                                <Input
                                  value={currentRemarks}
                                  onChange={(e) => {
                                    setAdvanceItemRemarks((prev) => ({
                                      ...prev,
                                      [itemId]: e.target.value,
                                    }));
                                  }}
                                  placeholder="Enter remarks"
                                  disabled={!isSelected}
                                />
                              </Tooltip>
                            );
                          },
                        },
                      ]}
                    />
                    <div className="mt-3 pt-2 border-top">
                      <div className="d-flex justify-content-between">
                        <span className="fw-bold">
                          Selected Advance Amount:
                        </span>
                        <strong className="text-info">
                          ₹
                          {workOrder.advanceItems
                            .filter((item: any) =>
                              selectedAdvanceItems.has(
                                item.workOrderAdvanceDeductionId?.toString() ||
                                item.id?.toString() ||
                                ""
                              )
                            )
                            .reduce((sum: number, item: any) => {
                              const itemId =
                                item.workOrderAdvanceDeductionId?.toString() ||
                                item.id?.toString() ||
                                "";
                              const amount =
                                advanceItemAmounts[itemId] !== undefined
                                  ? advanceItemAmounts[itemId]
                                  : item.totalAmount || 0;
                              return sum + amount;
                            }, 0)
                            .toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                        </strong>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-muted small text-center p-3">
                    No advance items added. Click "Add Advance" from the actions
                    menu to add items.
                  </div>
                )}
              </div>
            </div>

            {/* Real-time Calculations Display */}
            <div className="mt-3">
              <Row gutter={[16, 16]}>
                {/* Live RA Bill Calculations - Left Side */}
                <Col xs={24} lg={14}>
                  <div
                    className="p-3 border rounded bg-light"
                    style={{ width: "100%", height: "100%" }}
                  >
                    <div className="fw-bold text-primary mb-2">
                      Current Bill Summary
                    </div>
                    {(() => {
                      // Calculate overall totals for current bill
                      const hasQuantities = workLines.some((line) => {
                        if (
                          line.hasStages &&
                          line.stages &&
                          line.stages.length > 0
                        ) {
                          return line.stages.some(
                            (stage) => (stage.thisBillQuantity || 0) > 0
                          );
                        }
                        return (line.thisBillQuantity || 0) > 0;
                      });

                      if (!hasQuantities) {
                        return (
                          <div className="text-muted small">
                            Enter Bill Qty to see calculations
                          </div>
                        );
                      }

                      const workLinesSubtotal = workLines.reduce(
                        (sum, line) => {
                          // For stage-wise lines, use lineSubtotal (sum of stage amounts)
                          if (
                            line.hasStages &&
                            line.stages &&
                            line.stages.length > 0
                          ) {
                            return sum + (line.lineSubtotal || 0);
                          }
                          // For non-stage lines, use thisBillQuantity * rate
                          const qty = line.thisBillQuantity || 0;
                          const rate = line.rate || 0;
                          return sum + qty * rate;
                        },
                        0
                      );

                      // Calculate additional charges total
                      const additionalChargesTotal = additionalCharges.reduce(
                        (sum, charge) => sum + (charge.amount || 0),
                        0
                      );

                      // Subtotal = work lines subtotal only (additional charges added to Current Bill Total)
                      const billSubtotal = workLinesSubtotal;

                      // Calculate GST slab-wise breakdown for current bill
                      const billGstSlabBreakdown: {
                        [key: string]: {
                          cgstRate: number;
                          sgstRate: number;
                          igstRate: number;
                          taxAmount: number;
                        };
                      } = {};

                      let billLineLevelTaxAmount = 0;

                      workLines.forEach((line: any) => {
                        // For stage-wise lines, check if any stage has amount > 0
                        // For non-stage lines, check thisBillQuantity
                        let lineValue = 0;
                        let hasValue = false;

                        if (
                          line.hasStages &&
                          line.stages &&
                          line.stages.length > 0
                        ) {
                          lineValue = line.lineSubtotal || 0;
                          hasValue = lineValue > 0;
                        } else {
                          const qty = line.thisBillQuantity || 0;
                          lineValue = qty * line.rate;
                          hasValue = qty > 0;
                        }

                        if (
                          hasValue &&
                          line.taxPercentage &&
                          line.taxPercentage > 0
                        ) {
                          const rate = line.taxPercentage;
                          const rateKey = `${rate}%`;

                          if (!billGstSlabBreakdown[rateKey]) {
                            billGstSlabBreakdown[rateKey] = {
                              cgstRate: 0,
                              sgstRate: 0,
                              igstRate: 0,
                              taxAmount: 0,
                            };
                          }

                          // Calculate GST amounts based on line value
                          if (line.igstRate && line.igstRate > 0) {
                            const igstAmt = (lineValue * line.igstRate) / 100;
                            billGstSlabBreakdown[rateKey].igstRate += igstAmt;
                            billLineLevelTaxAmount += igstAmt;
                          } else {
                            const cgstAmt =
                              (lineValue * (line.cgstRate || 0)) / 100;
                            const sgstAmt =
                              (lineValue * (line.sgstRate || 0)) / 100;
                            billGstSlabBreakdown[rateKey].cgstRate += cgstAmt;
                            billGstSlabBreakdown[rateKey].sgstRate += sgstAmt;
                            billLineLevelTaxAmount += cgstAmt + sgstAmt;
                          }
                          billGstSlabBreakdown[rateKey].taxAmount +=
                            (lineValue * line.taxPercentage) / 100;
                        }
                      });

                      // Get TDS/TCS rates from work order
                      const billTdsRate = workOrder?.tdsRate || 0;
                      const billTdsTaxName = workOrder?.tdsTaxName || "";
                      const billTcsRate = workOrder?.tcsRate || 0;
                      const billTcsTaxName = workOrder?.tcsTaxName || "";

                      // Gross Total = Subtotal + GST (without TCS)
                      const billGrossTotal =
                        billSubtotal + billLineLevelTaxAmount;

                      // TCS calculation - TCS is calculated on Gross Total
                      const billTcsAmount =
                        billTcsRate && billGrossTotal > 0
                          ? (billGrossTotal * billTcsRate) / 100
                          : 0;

                      // TDS is calculated on subtotal only
                      const billTdsAmount =
                        billTdsRate && billSubtotal > 0
                          ? (billSubtotal * billTdsRate) / 100
                          : 0;

                      // Payment Deductions (Mobilization, Retention, On Hold - excluding TDS)
                      // Always calculate based on current bill subtotal (Pre-tax)
                      const billPaymentDeductionsArray = deductions
                        .filter((d) => d.type !== "TDS") // Exclude TDS only
                        .map((deduction) => {
                          let amount = 0;
                          let capInfo: {
                            hasCap: boolean;
                            capPercent: number;
                            capLimit: number;
                            scenario: string;
                            normalRecovery: number;
                          } | null = null;

                          if (deduction.type === "Mobilization") {
                            // Mobilization recovery with cap logic
                            if (
                              billSubtotal > 0 &&
                              deduction.balanceMobilizationRecovery > 0 &&
                              deduction.recoveryPercentRate > 0 &&
                              deduction.recoveryPercentRate <= 100
                            ) {
                              const recoveryCapPercent =
                                deduction.recoveryCapPercent || 0;
                              const remainingAdvance =
                                deduction.balanceMobilizationRecovery;
                              const normalRate =
                                (billSubtotal *
                                  deduction.recoveryPercentRate) /
                                100;

                              if (recoveryCapPercent > 0 && workOrder) {
                                const woTotalValue =
                                  workOrder.woTotalValue || 0;
                                const previousBilled =
                                  workOrder.previousRABillsValue || 0;
                                const capLimitSubtotal =
                                  (woTotalValue * recoveryCapPercent) / 100;
                                const remainingBeforeCap =
                                  capLimitSubtotal - previousBilled;

                                capInfo = {
                                  hasCap: true,
                                  capPercent: recoveryCapPercent,
                                  capLimit: capLimitSubtotal,
                                  scenario: "",
                                  normalRecovery: Math.min(
                                    normalRate,
                                    remainingAdvance
                                  ),
                                };

                                if (remainingBeforeCap <= 0) {
                                  amount = 0;
                                  capInfo.scenario = "cap_exceeded";
                                } else {
                                  const cumulativeWithCurrent =
                                    previousBilled + billSubtotal;
                                  const willReachCap =
                                    cumulativeWithCurrent >= capLimitSubtotal;

                                  if (willReachCap) {
                                    amount = Math.min(
                                      billSubtotal,
                                      remainingAdvance,
                                      remainingBeforeCap
                                    );
                                    capInfo.scenario = "last_bill";
                                  } else {
                                    const normalPossibleInCap =
                                      (remainingBeforeCap *
                                        deduction.recoveryPercentRate) /
                                      100;
                                    const needsAcceleration =
                                      remainingAdvance > normalPossibleInCap;

                                    if (needsAcceleration) {
                                      const windowAfterBill = Math.max(
                                        0,
                                        remainingBeforeCap - billSubtotal
                                      );
                                      const maxSafeAfterBill =
                                        (windowAfterBill *
                                          deduction.recoveryPercentRate) /
                                        100;
                                      const minRequired = Math.max(
                                        0,
                                        remainingAdvance - maxSafeAfterBill
                                      );
                                      const normalRecovery = normalRate;
                                      const smartRecovery = Math.max(
                                        minRequired,
                                        normalRecovery
                                      );
                                      amount = Math.min(
                                        smartRecovery,
                                        billSubtotal,
                                        remainingAdvance,
                                        remainingBeforeCap
                                      );
                                      capInfo.scenario = "accelerated";
                                    } else {
                                      const normalRecovery = normalRate;
                                      amount = Math.min(
                                        normalRecovery,
                                        remainingAdvance,
                                        remainingBeforeCap
                                      );
                                      capInfo.scenario = "normal";
                                    }
                                  }
                                }
                              } else {
                                amount =
                                  (billSubtotal *
                                    deduction.recoveryPercentRate) /
                                  100;
                                amount = Math.min(amount, remainingAdvance);
                              }
                            }
                            // If conditions not met, amount stays 0
                          } else if (deduction.type === "Retention") {
                            // Always calculate on Pre-tax (subtotal)
                            if (
                              deduction.percentValue &&
                              deduction.percentValue > 0 &&
                              billSubtotal > 0
                            ) {
                              amount =
                                (billSubtotal * deduction.percentValue) / 100;
                            } else if (
                              !deduction.percentValue &&
                              deduction.calculatedAmount &&
                              workOrder?.woTotalValue &&
                              billSubtotal > 0
                            ) {
                              // Calculate percentage from fixed amount
                              const derivedPercentage =
                                (deduction.calculatedAmount /
                                  workOrder.woTotalValue) *
                                100;
                              amount = (billSubtotal * derivedPercentage) / 100;
                            } else {
                              amount = deduction.calculatedAmount || 0;
                            }
                          } else if (
                            deduction.type === "On Hold" ||
                            deduction.type === "On-Hold"
                          ) {
                            // Always calculate on Pre-tax (subtotal)
                            if (
                              deduction.calculationType === "Percentage" &&
                              deduction.percentValue &&
                              deduction.percentValue > 0 &&
                              billSubtotal > 0
                            ) {
                              amount =
                                (billSubtotal * deduction.percentValue) / 100;
                            } else if (
                              !deduction.percentValue &&
                              deduction.calculatedAmount &&
                              workOrder?.woTotalValue &&
                              billSubtotal > 0
                            ) {
                              // Calculate percentage from fixed amount
                              const derivedPercentage =
                                (deduction.calculatedAmount /
                                  workOrder.woTotalValue) *
                                100;
                              amount = (billSubtotal * derivedPercentage) / 100;
                            } else {
                              amount =
                                deduction.calculatedAmount ||
                                deduction.amount ||
                                0;
                            }
                          }

                          return { ...deduction, currentBillAmount: amount, capInfo };
                        });

                      const billPaymentDeductions =
                        billPaymentDeductionsArray.reduce(
                          (sum, d) => sum + (d.currentBillAmount || 0),
                          0
                        );
                      console.log("HTML::::deductions", deductions);
                      // Calculate Mobilization Interest
                      const mobilizationDed = deductions.find(
                        (d) => d.type === "Mobilization"
                      );
                      let billInterestAmount = 0;
                      let billInterestDays = 0;
                      let billInterestFromDate = "";
                      let billInterestToDate = "";
                      const billInterestCharged =
                        mobilizationDed?.interestCharged || false;
                      const billInterestRate =
                        mobilizationDed?.interestRate || 0;

                      if (
                        billInterestCharged &&
                        billInterestRate > 0 &&
                        mobilizationDed?.balanceMobilizationRecovery > 0
                      ) {
                        // Use lastRaBillDate if available, otherwise use contractDate
                        const fromDate = workOrder?.lastRaBillDate
                          ? dayjs(workOrder.lastRaBillDate)
                          : workOrder?.contractDate
                            ? dayjs(workOrder.contractDate)
                            : null;
                        const toDate = dayjs(); // Current date

                        if (fromDate && fromDate.isValid()) {
                          billInterestDays = toDate.diff(fromDate, "day");
                          billInterestFromDate = fromDate.format("YYYY-MM-DD");
                          billInterestToDate = toDate.format("YYYY-MM-DD");

                          if (billInterestDays > 0) {
                            // Interest = Balance * Rate% * Days / 365
                            billInterestAmount =
                              (mobilizationDed.balanceMobilizationRecovery *
                                billInterestRate *
                                billInterestDays) /
                              (100 * 365);
                          }
                        }
                      }

                      // Calculate selected Riskflass and Advance items totals using edited amounts
                      const selectedRiskflassTotal =
                        workOrder?.riskflassItems
                          ?.filter((item: any) =>
                            selectedRiskflassItems.has(
                              item.workOrderAdvanceDeductionId?.toString() ||
                              item.id?.toString() ||
                              ""
                            )
                          )
                          .reduce((sum: number, item: any) => {
                            const itemId =
                              item.workOrderAdvanceDeductionId?.toString() ||
                              item.id?.toString() ||
                              "";
                            const amount =
                              riskflassItemAmounts[itemId] !== undefined
                                ? riskflassItemAmounts[itemId]
                                : item.totalAmount || 0;
                            return sum + amount;
                          }, 0) || 0;

                      const selectedAdvanceTotal =
                        workOrder?.advanceItems
                          ?.filter((item: any) =>
                            selectedAdvanceItems.has(
                              item.workOrderAdvanceDeductionId?.toString() ||
                              item.id?.toString() ||
                              ""
                            )
                          )
                          .reduce((sum: number, item: any) => {
                            const itemId =
                              item.workOrderAdvanceDeductionId?.toString() ||
                              item.id?.toString() ||
                              "";
                            const amount =
                              advanceItemAmounts[itemId] !== undefined
                                ? advanceItemAmounts[itemId]
                                : item.totalAmount || 0;
                            return sum + amount;
                          }, 0) || 0;

                      const totalSelectedDeductions =
                        selectedRiskflassTotal + selectedAdvanceTotal;

                      // Net Payable = Gross Total + TCS + Interest - TDS - Payment Deductions
                      const billNetPayable =
                        billGrossTotal +
                        billTcsAmount +
                        billInterestAmount -
                        billTdsAmount -
                        billPaymentDeductions;

                      // Current Bill Total = Net Payable + Additional Charges - Selected Riskflass/Advance
                      const currentBillTotal =
                        billNetPayable +
                        additionalChargesTotal -
                        totalSelectedDeductions;

                      return (
                        <div className="p-2 border rounded bg-white">
                          <div className="small">
                            <div className="d-flex justify-content-between mb-2">
                              <span>
                                <strong>Subtotal:</strong>
                              </span>
                              <strong>
                                ₹{workLinesSubtotal.toLocaleString("en-IN")}
                              </strong>
                            </div>

                            {/* GST Slab-wise Breakdown */}
                            {Object.keys(billGstSlabBreakdown).length > 0 &&
                              Object.entries(billGstSlabBreakdown).map(
                                ([rate, amounts]) => (
                                  <div key={rate} className="mb-2">
                                    <div className="d-flex justify-content-between mb-1">
                                      <span style={{ color: "#666" }}>
                                        GST @ {rate}:
                                      </span>
                                      <span></span>
                                    </div>
                                    {amounts.igstRate > 0 ? (
                                      <div
                                        className="d-flex justify-content-between"
                                        style={{ paddingLeft: "20px" }}
                                      >
                                        <span>IGST:</span>
                                        <span>
                                          ₹{amounts.igstRate.toLocaleString("en-IN")}
                                        </span>
                                      </div>
                                    ) : (
                                      <>
                                        <div
                                          className="d-flex justify-content-between"
                                          style={{ paddingLeft: "20px" }}
                                        >
                                          <span>CGST:</span>
                                          <span>
                                            ₹{amounts.cgstRate.toLocaleString("en-IN")}
                                          </span>
                                        </div>
                                        <div
                                          className="d-flex justify-content-between"
                                          style={{ paddingLeft: "20px" }}
                                        >
                                          <span>SGST:</span>
                                          <span>
                                            ₹{amounts.sgstRate.toLocaleString("en-IN")}
                                          </span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )
                              )}

                            <div className="d-flex justify-content-between mb-2">
                              <span>
                                <strong>Gross Total:</strong>
                              </span>
                              <strong className="text-success">
                                ₹{billGrossTotal.toLocaleString("en-IN")}
                              </strong>
                            </div>

                            {billTcsAmount > 0 && (
                              <div className="d-flex justify-content-between mb-2">
                                <span>
                                  TCS Amount: ({billTcsTaxName} -{" "}
                                  {billTcsRate.toFixed(2)}%)
                                </span>
                                <strong className="text-success">
                                  ₹{billTcsAmount.toLocaleString("en-IN")}
                                </strong>
                              </div>
                            )}

                            {/* Mobilization Interest */}
                            {billInterestCharged && billInterestAmount > 0 && (
                              <div className="mb-2">
                                <div className="d-flex justify-content-between mb-1">
                                  <span>
                                    Mobilization Interest ({billInterestRate}%
                                    for {billInterestDays} days)
                                  </span>
                                  <strong className="text-success">
                                    +₹{billInterestAmount.toLocaleString("en-IN")}
                                  </strong>
                                </div>
                                {billInterestFromDate && billInterestToDate && (
                                  <div
                                    style={{
                                      paddingLeft: "20px",
                                      fontSize: "11px",
                                      color: "#666",
                                    }}
                                  >
                                    Period:{" "}
                                    {dayjs(billInterestFromDate).format(
                                      "DD-MMM-YYYY"
                                    )}{" "}
                                    to{" "}
                                    {dayjs(billInterestToDate).format(
                                      "DD-MMM-YYYY"
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {billTdsAmount > 0 && (
                              <div className="d-flex justify-content-between mb-2">
                                <span>
                                  TDS Deduction: ({billTdsTaxName} -{" "}
                                  {billTdsRate.toFixed(2)}%)
                                </span>
                                <strong className="text-danger">
                                  -₹{billTdsAmount.toLocaleString("en-IN")}
                                </strong>
                              </div>
                            )}

                            {/* Payment Deductions Breakdown (Pre-tax) */}
                            {billPaymentDeductions > 0 && (
                              <>
                                <div className="mb-1" style={{ color: "#666" }}>
                                  <span>Payment Deductions:</span>
                                </div>
                                {billPaymentDeductionsArray.map((deduction) => {
                                  // Calculate display percentage
                                  let displayPercentage =
                                    deduction.percentValue;

                                  // For Mobilization, use recoveryPercentRate
                                  if (deduction.type === "Mobilization") {
                                    displayPercentage =
                                      deduction.recoveryPercentRate || 0;
                                  } else if (
                                    !displayPercentage &&
                                    deduction.calculatedAmount &&
                                    billSubtotal > 0
                                  ) {
                                    displayPercentage =
                                      (deduction.calculatedAmount /
                                        billSubtotal) *
                                      100;
                                  }

                                  return (
                                    <React.Fragment key={deduction.id}>
                                      <div
                                        className="d-flex justify-content-between mb-1"
                                        style={{ paddingLeft: "20px" }}
                                      >
                                        <span>
                                          {deduction.type}
                                          {displayPercentage
                                            ? ` (${displayPercentage.toFixed(
                                              2
                                            )}%)`
                                            : ""}
                                          :
                                        </span>
                                        <span className="text-danger">
                                          -₹
                                          {(
                                            deduction.currentBillAmount || 0
                                          ).toLocaleString("en-IN")}
                                        </span>
                                      </div>
                                      {/* Recovery Cap Info Note */}
                                      {deduction.type === "Mobilization" &&
                                        deduction.capInfo?.hasCap && (
                                          <div
                                            style={{
                                              paddingLeft: "20px",
                                              marginBottom: "6px",
                                              fontSize: "11px",
                                              padding: "4px 8px 4px 20px",
                                              borderRadius: "4px",
                                              background:
                                                deduction.capInfo.scenario ===
                                                "cap_exceeded"
                                                  ? "#fff2f0"
                                                  : deduction.capInfo
                                                      .scenario ===
                                                    "last_bill"
                                                  ? "#fff7e6"
                                                  : deduction.capInfo
                                                      .scenario ===
                                                    "accelerated"
                                                  ? "#e6f7ff"
                                                  : "#f6ffed",
                                              color:
                                                deduction.capInfo.scenario ===
                                                "cap_exceeded"
                                                  ? "#cf1322"
                                                  : "#595959",
                                            }}
                                          >
                                            {deduction.capInfo.scenario ===
                                              "cap_exceeded" && (
                                              <>
                                                Recovery cap of{" "}
                                                {deduction.capInfo.capPercent}%
                                                (₹
                                                {deduction.capInfo.capLimit.toLocaleString("en-IN")}
                                                ) has been reached. No further
                                                recovery applicable.
                                              </>
                                            )}
                                            {deduction.capInfo.scenario ===
                                              "last_bill" && (
                                              <>
                                                This bill reaches the{" "}
                                                {deduction.capInfo.capPercent}%
                                                recovery cap (₹
                                                {deduction.capInfo.capLimit.toLocaleString("en-IN")}
                                                ). Remaining advance recovered
                                                in full.
                                              </>
                                            )}
                                            {deduction.capInfo.scenario ===
                                              "accelerated" && (
                                              <>
                                                Recovery adjusted from ₹
                                                {deduction.capInfo.normalRecovery.toLocaleString("en-IN")}{" "}
                                                to ₹
                                                {(
                                                  deduction.currentBillAmount ||
                                                  0
                                                ).toLocaleString("en-IN")}{" "}
                                                to ensure full recovery before{" "}
                                                {deduction.capInfo.capPercent}%
                                                cap (₹
                                                {deduction.capInfo.capLimit.toLocaleString("en-IN")}
                                                ).
                                              </>
                                            )}
                                            {deduction.capInfo.scenario ===
                                              "normal" && (
                                              <>
                                                Recovery cap:{" "}
                                                {deduction.capInfo.capPercent}%
                                                of WO (₹
                                                {deduction.capInfo.capLimit.toLocaleString("en-IN")}
                                                )
                                              </>
                                            )}
                                          </div>
                                        )}
                                    </React.Fragment>
                                  );
                                })}
                              </>
                            )}
                            <div className="d-flex justify-content-between mb-2">
                              <span>
                                <strong>Current Bill Total:</strong>
                              </span>
                              <strong className="text-primary">
                                ₹{billNetPayable.toLocaleString("en-IN")}
                              </strong>
                            </div>

                            {/* Additional Charges - shown after Current Bill Total */}
                            {additionalChargesTotal > 0 && (
                              <div className="d-flex justify-content-between mb-2">
                                <span>Additional Charges:</span>
                                <strong className="text-info">
                                  +₹{additionalChargesTotal.toLocaleString("en-IN")}
                                </strong>
                              </div>
                            )}

                            {/* Selected Riskflass & Advance Deductions */}
                            {totalSelectedDeductions > 0 && (
                              <>
                                <div className="mb-1" style={{ color: "#666" }}>
                                  <span>Selected Deductions:</span>
                                </div>
                                {selectedRiskflassTotal > 0 && (
                                  <div
                                    className="d-flex justify-content-between mb-1"
                                    style={{ paddingLeft: "20px" }}
                                  >
                                    <span>Riskflags:</span>
                                    <span className="text-danger">
                                      -₹
                                      {selectedRiskflassTotal.toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                )}
                                {selectedAdvanceTotal > 0 && (
                                  <div
                                    className="d-flex justify-content-between mb-2"
                                    style={{ paddingLeft: "20px" }}
                                  >
                                    <span>Additional Advances:</span>
                                    <span className="text-danger">
                                      -₹{selectedAdvanceTotal.toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Final Total with Additional Charges and Deductions */}
                            {(additionalChargesTotal > 0 ||
                              totalSelectedDeductions > 0) && (
                                <div
                                  className="d-flex justify-content-between pt-2 border-top"
                                  style={{ marginTop: "8px" }}
                                >
                                  <span>
                                    <strong>Total Billed Amount:</strong>
                                  </span>
                                  <strong
                                    className="text-success"
                                    style={{ fontSize: "14px" }}
                                  >
                                    ₹{currentBillTotal.toLocaleString("en-IN")}
                                  </strong>
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </Col>

                {/* Additional Charges - Right Side */}
                <Col xs={24} lg={10}>
                  <div
                    className="p-3 border rounded bg-light"
                    style={{ width: "100%", height: "100%" }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="fw-bold text-primary">
                        Additional Charges
                      </div>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleAddAdditionalCharge}
                      >
                        <PlusOutlined /> Add Charge
                      </Button>
                    </div>

                    {additionalCharges.length === 0 ? (
                      <div className="text-muted small text-center p-3">
                        No additional charges added. Click "Add Charge" to add
                        transportation, handling fees, or other charges.
                      </div>
                    ) : (
                      <div>
                        {additionalCharges.map((charge, index) => (
                          <div
                            key={charge.id}
                            className="p-2 mb-2 border rounded bg-white"
                          >
                            <Row gutter={[8, 8]} align="middle">
                              <Col xs={24} sm={12}>
                                <Input
                                  placeholder="Charge Name"
                                  value={charge.chargeName}
                                  onChange={(e) =>
                                    handleUpdateAdditionalCharge(
                                      charge.id,
                                      "chargeName",
                                      e.target.value
                                    )
                                  }
                                  style={{ width: "100%" }}
                                />
                              </Col>
                              <Col xs={20} sm={10}>
                                <InputNumber
                                  placeholder="Amount"
                                  value={charge.amount}
                                  onChange={(value) =>
                                    handleUpdateAdditionalCharge(
                                      charge.id,
                                      "amount",
                                      value || 0
                                    )
                                  }
                                  formatter={indianCurrencyFormatter}
                                  parser={indianCurrencyParser}
                                  style={{ width: "100%" }}
                                  min={0}
                                />
                              </Col>
                              <Col xs={4} sm={2}>
                                <Button
                                  color="danger"
                                  size="sm"
                                  className="btn-icon"
                                  onClick={() =>
                                    handleRemoveAdditionalCharge(charge.id)
                                  }
                                >
                                  <DeleteOutlined />
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        ))}

                        {/* Total Additional Charges */}
                        <div className="mt-3 pt-2 border-top">
                          <div className="d-flex justify-content-between">
                            <span className="fw-bold">
                              Total Additional Charges:
                            </span>
                            <strong className="text-info">
                              ₹{getTotalAdditionalCharges().toLocaleString("en-IN")}
                            </strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Card>

          {/* Deductions */}
          {/* <Card title="Deductions & Recoveries" className="mb-4">
            <Row gutter={[24, 16]}>
              {deductions.map((deduction: any) => (
                <Col xs={24} lg={6} key={deduction.id}>
                  <div className="border rounded p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-bold">{deduction.type}</div>
                      {!deduction.isManual && <Tag color="blue">Auto</Tag>}
                    </div>

                    <div
                      className="text-muted small mb-2"
                      style={{ minHeight: "32px" }}
                    >
                      {deduction.type === "Mobilization" && (
                        <div>
                          <div>
                            Max: ₹{(deduction.amount || 0).toLocaleString("en-IN")}
                          </div>
                          <div className="text-info">
                            Remaining balance limit
                          </div>
                        </div>
                      )}
                      {deduction.type === "Retention" && (
                        <div>
                          <div>
                            @ {deduction.percentage}% on {deduction.taxableBase}
                          </div>
                          <div className="text-info">Auto calculated</div>
                        </div>
                      )}
                      {(deduction.type === "On Hold" ||
                        deduction.type === "On-Hold") && (
                        <div>
                          <div>
                            {deduction.calculationType === "Percentage"
                              ? `@ ${deduction.percentage}% on Subtotal`
                              : "Flat amount"}
                          </div>
                          <div className="text-info">WO policy based</div>
                        </div>
                      )}
                      {deduction.type === "TDS" && (
                        <div>
                          <div>
                            @ {deduction.percentage}% ({deduction.tdsSection})
                          </div>
                          <div className="text-info">
                            On {deduction.taxableBase}
                          </div>
                          <div className="text-warning small">
                            Above ₹
                            {(deduction.thresholdAmount || 0).toLocaleString("en-IN")}
                          </div>
                        </div>
                      )}
                    </div>

                    <InputNumber
                      style={{ width: "100%" }}
                      value={deduction.amount}
                      onChange={(value) =>
                        handleDeductionChange(value, deduction.id)
                      }
                      formatter={indianCurrencyFormatter}
                      parser={indianCurrencyParser}
                      disabled={
                        deduction.type === "Retention" ||
                        deduction.type === "TDS" ||
                        ((deduction.type === "On Hold" ||
                          deduction.type === "On-Hold") &&
                          deduction.calculationType === "Percentage")
                      }
                      max={
                        deduction.type === "Mobilization"
                          ? deduction.maxAmount
                          : undefined
                      }
                    />

                    {deduction.remarks && (
                      <div
                        className="text-muted small mt-2"
                        style={{ fontSize: "11px" }}
                      >
                        {deduction.remarks}
                      </div>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </Card> */}

          {/* Totals */}
          {/* <Card title="Bill Totals" className="mb-4">
            <Row gutter={[24, 16]} className="text-center">
              <Col xs={12} lg={3}>
                <div className="border rounded p-3">
                  <div className="text-muted small">Subtotal (excl. tax)</div>
                  <div className="fw-bold h5 mb-0 mt-3">
                    {formatCurrency(totals?.subtotalExclTax || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={3}>
                <div className="border rounded p-3">
                  <div className="text-muted small">GST Total</div>
                  <div className="fw-bold h5 mb-0 mt-3 text-warning">
                    {formatCurrency(totals?.taxTotal || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={3}>
                <div className="border rounded p-3">
                  <div className="text-muted small">Gross Total</div>
                  <div className="fw-bold h5 mb-0 mt-3">
                    {formatCurrency(totals?.grossTotal || 0, "INR")}
                  </div>
                </div>
              </Col>
              <Col xs={12} lg={3}>
                <div className="border rounded p-3">
                  <div className="text-muted small">Total Deductions</div>
                  <div className="fw-bold h5 mb-0 text-danger mt-3">
                    -{formatCurrency(totals?.totalDeductions || 0, "INR")}
                  </div>
                </div>
              </Col>
            </Row>
            <Divider />
            <div className="">
              <div
                className="fw-bold text-success"
                style={{ fontSize: "20px" }}
              >
                Net Payable: {formatCurrency(totals?.netPayable || 0, "INR")}
              </div>
            </div>
          </Card> */}

          {/* Notes & Attachments */}
          <Card title="Notes & Attachments" className="mb-4">
            <Row gutter={[24, 16]}>
              <Col xs={24} lg={12}>
                <Form.Item label="Notes">
                  <TextArea
                    rows={4}
                    placeholder="Enter any additional notes..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item label="Attachments">
                  <Upload.Dragger
                    multiple
                    beforeUpload={(file) => {
                      setUploadedFiles((prev) => [...prev, file]);
                      return false; // Prevent automatic upload
                    }}
                    onRemove={(file) => {
                      setUploadedFiles((prev) =>
                        prev.filter((f) => f.uid !== file.uid)
                      );
                    }}
                    fileList={uploadedFiles.map((file) => ({
                      uid: file.uid,
                      name: file.name,
                      status: "done" as const,
                    }))}
                  >
                    <p className="ant-upload-drag-icon">
                      <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag files to this area to upload
                    </p>
                    <p className="ant-upload-hint">
                      Support for measurement sheets, photos, and other
                      supporting documents
                    </p>
                  </Upload.Dragger>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Action Buttons */}
          <Card>
            <div className="text-end">
              <Space size="large">
                <Button
                  color="primary"
                  type="button"
                  icon={<SendOutlined />}
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={hasValidationErrors()}
                >
                  {approvalRequired === "true"
                    ? "Submit for Approval"
                    : "Submit"}
                </Button>
              </Space>
            </div>
          </Card>
        </div>
      </div>

      {/* Approval Drawer */}
      {approvals.open && (
        <WorkOrderApprovalDrawer
          open={approvals.open}
          onClose={() => setApprovals((pre) => ({ ...pre, open: false }))}
          onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
            setApprovals({
              open: false,
              data: data,
            });
            setIsStrictApproval(isStrictApprovalFromDrawer);
          }}
          initialStrictApproval={isStrictApproval}
          setApprovalSubmissionStatus={(status) => {
            if (status) {
              message.success("Approvals configured successfully");
            }
          }}
          approvalData={approvals.data}
          siteId={workOrder?.site || 0}
          moduleName="RA Bill"
        />
      )}

      {/* RA Bill Number Configuration Modal */}
      <Modal
        title="Configure RA Bill Number Preference"
        open={showNumberingModal}
        onCancel={() => setShowNumberingModal(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px" }}>
            <Button
              key="save"
              color="primary"
              onClick={() => {
                if (tempNumberingMode === "auto" && !currentSiteSequence) {
                  message.error("Auto-generation is not configured for this site.");
                  return;
                }

                setNumberingMode(tempNumberingMode);

                if (tempNumberingMode === "auto" && currentSiteSequence) {
                  const { prefix } = currentSiteSequence;
                  const raNumber = `${prefix}`;
                  setRABillNumber(raNumber);
                  form.setFieldsValue({ raBillNumber: raNumber });
                }
                else if (tempNumberingMode === "manual") {
                  setRABillNumber("");
                  form.setFieldsValue({ raBillNumber: "" });
                }
                setShowNumberingModal(false);
              }}
            >
              Save
            </Button>

            <Button
              key="cancel"
              color="primary"
              onClick={() => setShowNumberingModal(false)}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <div style={{ marginBottom: 20, lineHeight: "22px" }}>
          Your RA Bill numbers are currently set to auto-generate to save time.
          <br />
          Are you sure you want to change this setting?
        </div>

        {!workOrder?.site && (
          <div style={{ color: "#faad14", marginBottom: 16 }}>
            <InfoCircleOutlined /> Please select a site to check sequence configuration.
          </div>
        )}

        {workOrder?.site && !currentSiteSequence && (
          <div style={{ color: "#faad14", marginBottom: 16 }}>
            <InfoCircleOutlined />{" "}
            {activeSequences.length === 0
              ? "No sequences configured for RA Bills. Please set up a sequence first."
              : "No sequence configured for the selected site. Please set a sequence for this site."}
          </div>
        )}

        <Radio.Group
          onChange={(e) => setTempNumberingMode(e.target.value)}
          value={tempNumberingMode}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Radio value="auto" disabled={!canAuto}>
              <span style={{ fontWeight: 500 }}>
                Continue auto-generating RA Bill numbers
              </span>
            </Radio>

            <Radio value="manual">
              <span style={{ fontWeight: 500 }}>
                Enter RA Bill numbers manually
              </span>
            </Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </Content>
  );
};

export default CreateRABillDetails;
